// Borealis Reserve — i18n strings for public-facing pages (login, accept-invite, forgot password modal).
// The internal portal (dashboard, deposits, statements, admin) stays in Italian for v1.
// We translate emails via the backend (email_service.py).

export const SUPPORTED_LOCALES = ["it", "en", "no", "de"];
export const DEFAULT_LOCALE = "it";

export const LOCALE_LABELS = {
  it: "IT",
  en: "EN",
  no: "NO",
  de: "DE",
};

export function normalizeLocale(value) {
  if (!value) return DEFAULT_LOCALE;
  const v = String(value).toLowerCase().split("-")[0];
  return SUPPORTED_LOCALES.includes(v) ? v : DEFAULT_LOCALE;
}

const STRINGS = {
  it: {
    // Switcher
    "switcher.label": "Lingua",

    // Login page
    "login.brand_tagline": "Private Client Portal",
    "login.overline": "Accesso riservato",
    "login.heading_a": "La tua",
    "login.heading_em": "quiete",
    "login.heading_b": "ti attende.",
    "login.intro": "Solo membri invitati. Non esiste registrazione pubblica.",
    "login.email": "Email",
    "login.password": "Password",
    "login.cta": "Entra nel portale",
    "login.cta_loading": "Accesso in corso…",
    "login.forgot": "Password dimenticata?",
    "login.no_invite_q": "Non hai ancora ricevuto un invito?",
    "login.no_invite_a": "Scrivi a",
    "login.footer_brand": "Borealis Reserve · Private Club",
    "login.checking_session": "Verifica sessione…",

    // 2FA login challenge
    "login.twofa_overline": "Autenticazione a due fattori",
    "login.twofa_heading_a": "Inserisci il",
    "login.twofa_heading_em": "codice",
    "login.twofa_heading_b": "del tuo Authenticator.",
    "login.twofa_intro": "Apri l'app Authenticator (Google Authenticator, Authy, 1Password) e digita il codice a 6 cifre.",
    "login.twofa_code_label": "Codice a 6 cifre",
    "login.twofa_cta": "Verifica e accedi",
    "login.twofa_back": "Torna al login",

    // Accept invite page
    "invite.brand_tagline": "Attivazione invito",
    "invite.overline": "Benvenuto",
    "invite.heading_em": "imposta",
    "invite.heading_lead": "la tua password.",
    "invite.intro": "Stai attivando l'account per",
    "invite.field_name": "Nome completo",
    "invite.field_password": "Nuova password",
    "invite.field_confirm": "Conferma password",
    "invite.cta": "Attiva account ed entra",
    "invite.cta_loading": "Attivazione…",
    "invite.checking": "Verifica invito…",
    "invite.error_title": "Invito non valido",
    "invite.error_explainer": "Gli inviti sono nominativi e a uso unico, con validità 7 giorni.",
    "invite.error_assistance": "Per assistenza scrivi a",
    "invite.err_password_min": "La password deve avere almeno 8 caratteri.",
    "invite.err_password_mismatch": "Le password non coincidono.",
    "invite.greeting_named": "{name},",
    "invite.greeting_default": "Membro,",

    // Forgot password modal
    "forgot.overline": "Riservatezza assoluta",
    "forgot.title_a": "Recupero",
    "forgot.title_em": "password",
    "forgot.heading_a": "Non gestiamo il reset",
    "forgot.heading_em": "online",
    "forgot.heading_b": ".",
    "forgot.p1": "Per proteggere il tuo capitale, la password si reimposta esclusivamente attraverso un canale verificato con un membro del nostro team Riserva.",
    "forgot.how_overline": "Come procedere",
    "forgot.step1": "Scrivi da un'email già nota al tuo Relationship Manager",
    "forgot.step2": "Riceverai una chiamata di verifica entro 24 ore",
    "forgot.step3": "A verifica completata, ti consegniamo un nuovo invito a uso unico",
    "forgot.cta_mail": "Scrivi a clients@",
    "forgot.cta_compliance": "Compliance",
    "forgot.reminder_title": "Promemoria",
    "forgot.reminder_body": "non chiediamo mai password, OTP o documenti via email o telefono prima della verifica vocale del tuo Relationship Manager.",
    "forgot.close": "Chiudi",
    "forgot.form_email_label": "Email associata all'account",
    "forgot.form_submit": "Invia richiesta",
    "forgot.form_submitting": "Invio in corso…",
    "forgot.error_generic": "Si è verificato un errore. Riprova più tardi.",
    "forgot.confirmation_overline": "Richiesta inoltrata",
    "forgot.confirmation_p1": "Abbiamo ricevuto la tua richiesta. Un membro del team Compliance ti contatterà via telefono entro 24 ore per la verifica.",
    "forgot.confirmation_p2": "Se l'email indicata corrisponde a un account attivo, riceverai una conferma. In caso contrario, nessuna email sarà spedita per ragioni di sicurezza.",

    // Reset password page (consumes admin-issued recovery link)
    "reset.overline": "Recupero password",
    "reset.title_a": "Imposta nuova",
    "reset.title_em": "password",
    "reset.loading": "Verifica del link in corso…",
    "reset.invalid_title": "Link non valido",
    "reset.invalid_expired": "Questo link è scaduto. Contatta il tuo Relationship Manager per riceverne uno nuovo.",
    "reset.invalid_used": "Questo link è già stato utilizzato. Se non sei stato tu, contatta immediatamente legal@borealisreserve.com.",
    "reset.invalid_generic": "Il link non è valido. Verifica di aver copiato l'URL completo dall'email oppure richiedi un nuovo link.",
    "reset.back_to_login": "Torna all'accesso",
    "reset.heading_a": "Scegli una",
    "reset.heading_em": "nuova password",
    "reset.heading_b": ".",
    "reset.intro_for_email": "Stai impostando una nuova password per {email}.",
    "reset.new_password": "Nuova password",
    "reset.confirm_password": "Conferma nuova password",
    "reset.policy_hint": "Minimo 10 caratteri, con almeno una minuscola, una maiuscola, una cifra e un simbolo.",
    "reset.mismatch": "Le due password non corrispondono.",
    "reset.submit": "Imposta nuova password",
    "reset.submitting": "Aggiornamento in corso…",
    "reset.error_generic": "Si è verificato un errore. Riprova oppure contatta il tuo Relationship Manager.",
    "reset.done_title": "Password aggiornata ✓",
    "reset.done_p1": "La tua nuova password è attiva. Per ragioni di sicurezza tutte le sessioni precedenti sono state chiuse: effettua nuovamente l'accesso.",
    "reset.go_login": "Vai al login",

    // Change password page (forced after temp-pwd login)
    "change.overline": "Sicurezza dell'account",
    "change.title_a": "Cambio",
    "change.title_em": "password",
    "change.heading_mandatory": "Imposta una password personale.",
    "change.heading_regular": "Cambia la password.",
    "change.intro_mandatory": "Hai effettuato l'accesso con una password temporanea fornita dal nostro team. Per proseguire scegli una nuova password personale.",
    "change.intro_regular": "Aggiorna la password del tuo account. Dopo il cambio tutte le sessioni attive saranno chiuse.",
    "change.current_label": "Password attuale (o temporanea)",
    "change.new_label": "Nuova password",
    "change.confirm_label": "Conferma nuova password",
    "change.policy_hint": "Minimo 10 caratteri, con almeno una minuscola, una maiuscola, una cifra e un simbolo.",
    "change.mismatch": "Le due password non corrispondono.",
    "change.same_as_current": "La nuova password deve essere diversa da quella attuale.",
    "change.submit": "Aggiorna password",
    "change.submitting": "Aggiornamento in corso…",
    "change.error_generic": "Si è verificato un errore. Riprova.",

    // Header (client)
    "nav.dashboard": "Dashboard",
    "nav.positions": "Depositi",
    "nav.statements": "Estratti",
    "nav.logout": "Esci",
    "pwa.install": "Installa l'app",
    "pwa.ios_title": "Installa su iPhone / iPad",
    "pwa.ios_step1": "Tocca il pulsante Condividi nella barra di Safari (il quadrato con la freccia verso l'alto).",
    "pwa.ios_step2": "Scorri l'elenco e scegli «Aggiungi a schermata Home», poi conferma con «Aggiungi».",
    "pwa.ios_note": "L'app Borealis Reserve apparirà sulla schermata Home e si aprirà a schermo intero, come un'applicazione nativa.",
    "header.private_portal": "Private Portal",

    // Footer
    "footer.members_only": "© {year} Borealis Reserve · Riservato ai soli membri",
    "footer.licenses_title": "Licenze e domiciliazioni",
    "footer.broker_title": "Brokerage Firm regolamentata da FINRA",
    "footer.broker_subline": "ABG Securities, Inc. · Finanshuset (USA) Securities, Inc. · FIBA Nordic Securities, Inc. · ABG Sundal Collier Inc.",
    "footer.main_address": "Sede principale",
    "footer.investment_firm": "Impresa di investimento",
    "footer.registered_office": "Sede legale",
    "footer.city": "Città",
    "footer.country": "Stato",
    "footer.enrolment_no": "N. iscrizione",
    "footer.finanstilsynet_id": "Finanstilsynet ID",
    "footer.finra_link": "Profilo FINRA →",
    "footer.consob_link": "Consob →",
    "footer.finanstilsynet_link": "Finanstilsynet →",
    "footer.legal_note": "Borealis Reserve è un servizio riservato ai membri del club. Le condizioni di partecipazione e i rendimenti indicati nei materiali del portale non costituiscono offerta al pubblico né sollecitazione all'investimento ai sensi delle normative vigenti.",

    // Dashboard
    "dash.overline": "Benvenuto",
    "dash.greeting": "Buongiorno, {name}.",
    "dash.greeting_default": "Membro",
    "dash.intro": "Il tuo capitale lavora ogni secondo. Qui sotto trovi la fotografia in tempo reale del tuo conto.",
    "dash.loading": "Caricamento…",
    "dash.capital_managed": "Capitale gestito",
    "dash.on_n_deposits_one": "Su {n} deposito attivo.",
    "dash.on_n_deposits": "Su {n} depositi attivi.",
    "dash.updated_at": "Aggiornato al {date}.",
    "dash.today_accrued": "Maturato oggi",
    "dash.linear_accrual": "Maturazione lineare giornaliera",
    "dash.per_day": "{amount}/giorno",
    "dash.kpi_accrued": "Maturato a oggi",
    "dash.kpi_monthly": "Stima mensile",
    "dash.kpi_yearly": "Stima annuale",
    "dash.kpi_statements": "Estratti disponibili",
    "dash.kyc_overline": "Onboarding",
    "dash.kyc_title": "Completa il tuo dossier",
    "dash.kyc_progress": "{approved} di {total} documenti approvati",
    "dash.kyc_submitted": "{n} in revisione",
    "dash.kyc_rejected": "{n} da ricaricare",
    "dash.kyc_note": "L'accesso completo a piani, eventi e team si sblocca al completamento del KYC.",
    "dash.kyc_cta": "Completa ora",
    "dash.next_coupon": "Prossima cedola",
    "dash.principal": "Capitale",
    "dash.coupon_amount": "Importo cedola",
    "dash.your_deposits": "I tuoi depositi",
    "dash.see_all": "Vedi tutti →",
    "dash.net_rate": "Tasso netto",
    "dash.accrued": "Maturato",
    "dash.daily": "Al giorno",
    "dash.auto_reinvest_active": "Auto-reinvest attivo",
    "dash.deposit": "Deposito",
    "dash.days_progress": "{elapsed}/{total} gg",

    // Positions
    "pos.overline": "Posizioni",
    "pos.heading_a": "I tuoi",
    "pos.heading_em": "depositi",
    "pos.heading_b": ".",
    "pos.intro": "Maturazione lineare giornaliera, capitalizzazione solo se &quot;Auto-reinvest&quot; è attivo alla scadenza.",
    "pos.col_deposit": "Deposito",
    "pos.col_principal": "Capitale",
    "pos.col_gross_rate": "Tasso lordo",
    "pos.col_fee": "Commiss.",
    "pos.col_net_rate": "Tasso netto",
    "pos.col_start": "Decorrenza",
    "pos.col_end": "Scadenza",
    "pos.col_accrued": "Maturato netto",
    "pos.col_daily": "/Giorno",
    "pos.col_maturity_value": "Valore a scadenza",
    "pos.auto_reinvest_short": "Auto-reinvest",
    "pos.empty": "Nessun deposito ancora attivo.",

    // Statements
    "stmt.overline": "Documenti",
    "stmt.heading_a": "Estratti e",
    "stmt.heading_em": "certificazioni",
    "stmt.heading_b": ".",
    "stmt.intro": "Pubblicati dal back-office. Scaricali in PDF firmato per la tua contabilità o per il commercialista.",
    "stmt.col_period": "Periodo",
    "stmt.col_kind": "Tipo",
    "stmt.col_label": "Etichetta",
    "stmt.col_published": "Pubblicato",
    "stmt.col_actions": "Azioni",
    "stmt.kind_quarterly": "Trimestrale",
    "stmt.kind_monthly": "Mensile",
    "stmt.kind_annual": "Annuale",
    "stmt.kind_coupon": "Cedola",
    "stmt.label_default": "Estratto",
    "stmt.download": "Scarica",
    "stmt.empty": "Nessun estratto ancora disponibile.",

    // Bank details (client dashboard card)
    "bank.overline": "Coordinate bancarie",
    "bank.title": "Dove versare il capitale",
    "bank.intro": "Bonifica esclusivamente alle coordinate qui sotto, riservate al tuo conto.",
    "bank.empty_title": "In attesa di assegnazione",
    "bank.empty_body": "Il tuo Relationship Manager sta finalizzando le coordinate bancarie dedicate al tuo conto. Riceverai notifica appena saranno disponibili.",
    "bank.beneficiary": "Beneficiario",
    "bank.iban": "IBAN",
    "bank.bic": "BIC / SWIFT",
    "bank.bank": "Banca",
    "bank.reference": "Causale suggerita",
    "bank.copy": "Copia",
    "bank.copied": "Copiato",
    "bank.disclaimer": "Verifica sempre le coordinate con il tuo referente prima di disporre il bonifico. Borealis Reserve non chiede mai modifiche di IBAN via email.",

    // Explore (auth content)
    "nav.explore": "Esplora",
    "explore.philosophy": "Filosofia",
    "explore.compliance": "Compliance",
    "explore.faq": "FAQ",
    "explore.overline": "Sapere riservato",
    "explore.title": "Il club, in dettaglio.",
    "explore.intro": "Tre stanze interne dedicate ai membri: la nostra filosofia operativa, la struttura legale di vigilanza e le domande più frequenti prima di investire.",
    "explore.card_philosophy_desc": "Un modello scandinavo, applicato a un cliente latino. Come funziona Borealis in quattro passi.",
    "explore.card_compliance_desc": "Struttura legale, autorità di vigilanza, garanzie depositi, conflitti d'interesse, GDPR.",
    "explore.card_faq_desc": "Le sei domande prima di investire. Risposte dirette, senza piccolo asterisco.",
    "explore.read": "Approfondisci →",
    "explore.back": "← Torna a Esplora",

    // KYC
    "nav.kyc": "KYC",
    "kyc.overline": "Onboarding KYC",
    "kyc.title": "I tuoi documenti, in regola.",
    "kyc.intro": "Per attivare pienamente il tuo conto Borealis Reserve abbiamo bisogno che tu firmi alcuni documenti e ne carichi altri. Operazione una tantum, in tutta tranquillità.",
    "kyc.progress_label": "Avanzamento",
    "kyc.progress_value": "{approved} di {total} approvati",
    "kyc.complete_banner": "Onboarding completato. Il tuo conto è pienamente operativo.",
    "kyc.status_pending": "Da completare",
    "kyc.status_submitted": "In verifica",
    "kyc.status_approved": "Approvato",
    "kyc.status_rejected": "Da rivedere",
    "kyc.kind_sign": "Firma digitale",
    "kyc.kind_upload": "Upload documento",
    "kyc.action_view_template": "Apri template",
    "kyc.action_download_template": "Scarica template",
    "kyc.action_sign": "Firma digitalmente",
    "kyc.action_resign": "Firma di nuovo",
    "kyc.action_upload": "Carica file",
    "kyc.action_replace": "Sostituisci",
    "kyc.action_download_signed": "Scarica copia firmata",
    "kyc.action_download_uploaded": "Vedi documento caricato",
    "kyc.upload_accept": "PDF, JPG, PNG · max 12 MB",
    "kyc.uploading": "Caricamento…",
    "kyc.signing": "Firma in corso…",
    "kyc.last_comment": "Nota Compliance",
    "kyc.sign_modal_title": "Firma elettronica semplice",
    "kyc.sign_modal_intro": "Stai per apporre la tua firma elettronica al documento <strong>{title}</strong>. La firma verrà registrata con audit trail (timestamp UTC, IP, hash SHA-256) ai sensi del Regolamento UE 910/2014 (eIDAS).",
    "kyc.sign_modal_checkbox": "Dichiaro di aver letto, compreso e accettato integralmente il contenuto del documento.",
    "kyc.sign_modal_confirm": "Firma adesso",
    "kyc.sign_modal_cancel": "Annulla",
    "kyc.sign_modal_disclaimer": "La firma elettronica semplice produce gli effetti giuridici di una sottoscrizione scritta tra le parti.",

    // Yield calculator (initial-client dashboard widget)
    "calc.overline": "Simulatore di rendimento",
    "calc.title": "Stima il tuo orizzonte.",
    "calc.intro": "Sposta il cursore per simulare il rendimento atteso sui tuoi primi 12 mesi di giacenza. I valori riflettono i pacchetti correnti pubblicati dal team Borealis Reserve.",
    "calc.amount_label": "Capitale da conferire",
    "calc.duration_label": "Giacenza",
    "calc.duration_12": "12 mesi",
    "calc.duration_24": "24 mesi",
    "calc.duration_36": "36 mesi",
    "calc.duration_open": "Open-ended",
    "calc.tier_label": "Pacchetto applicabile",
    "calc.rate_gross": "Rendimento lordo atteso",
    "calc.rate_net": "Rendimento netto stimato",
    "calc.mngmt_fee": "Management fee",
    "calc.perf_fee": "Performance fee",
    "calc.lock_period": "Lock period",
    "calc.gross_profit": "Profitto lordo ({months}m)",
    "calc.net_profit": "Profitto netto ({months}m)",
    "calc.maturity_value": "Valore a scadenza",
    "calc.chart_title": "Crescita del capitale",
    "calc.chart_legend": "Proiezione {months} mesi",
    "calc.chart_open_note": "Proiezione 12 mesi indicativa",
    "calc.no_plan": "Per questo capitale non risulta attivo un pacchetto. Contatta il tuo Relationship Manager.",
    "calc.disclaimer": "Simulazione non vincolante a fini illustrativi. Le condizioni effettive sono quelle del pacchetto sottoscritto al momento del conferimento.",
    "disc.title": "Informazioni regolamentari",
    "disc.past": "Le performance passate non sono un indicatore affidabile dei risultati futuri.",
    "disc.risk": "Il capitale investito è a rischio. Il valore degli investimenti può diminuire oltre che aumentare.",
    "disc.mkt": "Comunicazione di marketing. Non costituisce consulenza finanziaria né offerta al pubblico.",
    "nav.plans": "Piani",
    "plans.overline": "Sottoscrizione",
    "plans.title": "Piani di investimento",
    "plans.intro": "Esplora i pacchetti a rendimento fisso riservati ai membri. Seleziona un piano per inviare una richiesta di sottoscrizione al team Finance & Compliance.",
    "plans.loading": "Caricamento piani…",
    "plans.kyc_title": "Esplora liberamente",
    "plans.kyc_body": "Sfoglia i pacchetti, scarica i KID e simula i rendimenti. Puoi inviare subito la richiesta di adesione: per attivarla ti chiederemo di completare la verifica KYC.",
    "plans.kyc_cta": "Vai al KYC",
    "plans.partner_title": "Modalità partner",
    "plans.partner_body": "Vetrina di presentazione: usa le schede prodotto, il simulatore di rendimenti e il KID per raccontare Borealis Reserve ai tuoi contatti. L'adesione ai piani è riservata ai clienti sottoscrittori.",
    "plans.partner_showcase_badge": "Solo presentazione",
    "plans.partner_your_code": "Il tuo codice",
    "plans.partner_copy_link": "Copia il tuo link →",
    "plans.range": "Fascia di conferimento",
    "plans.checkbox": "Ho letto le informazioni e desidero richiedere questo piano",
    "plans.request_cta": "Richiedi sottoscrizione",
    "plans.pending_badge": "Richiesta in attesa",
    "plans.amount_label": "Importo indicativo (facoltativo)",
    "plans.amount_help": "Entro la fascia del piano. L'importo definitivo sarà concordato con il team.",
    "plans.note_label": "Nota per il team (facoltativa)",
    "plans.modal_title": "Conferma richiesta di sottoscrizione",
    "plans.modal_body": "Cliccando su «Conferma e invia», la tua richiesta per il piano «{plan}» sarà inviata al team Finance & Compliance. Riceverai maggiori dettagli via email e telefono, incluse — in caso di accettazione — le coordinate per il bonifico. Nessun versamento è dovuto in questa fase.",
    "plans.modal_confirm": "Conferma e invia",
    "plans.modal_cancel": "Annulla",
    "plans.toast_ok": "Richiesta inviata. Il team Finance & Compliance ti contatterà a breve.",
    "plans.toast_err": "Impossibile inviare la richiesta. Riprova.",
    "plans.my_requests": "Le mie richieste",
    "plans.status_pending": "In attesa",
    "plans.status_awaiting_kyc": "In attesa di KYC",
    "plans.toast_ok_kyc": "Richiesta inviata. Completa ora la verifica KYC: appena approvata, la tua richiesta passerà al team Investimenti.",
    "plans.status_approved": "Approvata",
    "plans.status_rejected": "Non accolta",
    "plans.no_requests": "Non hai ancora inviato richieste.",
    "plans.kid_download": "Scarica KID (PDF)",
    "plans.kid_ack_label": "Ho letto e compreso il Documento contenente le informazioni chiave (KID) di questo prodotto",
    "plans.kid_modal_hint": "Prima di inviare la richiesta, consulta il documento informativo del prodotto (KID).",
    "plans.allocation_title": "Asset allocation",
    "plans.volatility_target": "Target di volatilità",
    "plans.allocation_show": "Visualizza Asset Allocation",
    "plans.allocation_hide": "Nascondi Asset Allocation",
    "plans.allocation_note": "Allocazione target indicativa a scopo illustrativo, ribilanciata periodicamente dal Comitato Investimenti di Borealis. Le ponderazioni effettive possono variare in base alle condizioni di mercato.",
    "alloc.global_equity_etf": "ETF Azionari Globali",
    "alloc.global_equity": "Azionario Globale",
    "alloc.investment_grade_bonds": "Obbligazioni Investment Grade",
    "alloc.money_market_short": "Monetario & Breve Durata",
    "alloc.money_market": "Monetario",
    "alloc.alternative_ucits_funds": "Fondi UCITS Alternativi",
    "alloc.alternative_ucits_strategies": "Strategie UCITS Alternative",
    "alloc.alternative_strategies": "Strategie Alternative",
    "alloc.gold_commodities": "Oro & Materie Prime",
    "alloc.commodities": "Materie Prime",
    "alloc.private_markets": "Mercati Privati",
    "alloc.private_equity": "Private Equity",
    "alloc.private_credit": "Private Credit",
    "alloc.real_assets": "Real Asset",
    "alloc.cash": "Liquidità",
    "alloc.tactical_cash": "Liquidità Tattica",
    "calc.months": "mesi",
    "calc.lock_open": "Open-ended",

    // Management team (post-KYC client section + admin)
    "team.overline": "Team di gestione",
    "team.title": "Le persone dietro al tuo capitale.",
    "team.intro": "I professionisti che strutturano la tua strategia. Profili regolati e verificabili presso FINRA.",
    "team.crd": "CRD#",
    "team.years_experience": "anni di esperienza",
    "team.finra_link": "Profilo FINRA →",
    "team.locked_title": "Sezione riservata",
    "team.locked_body": "Il team di gestione sarà visibile dopo il completamento del processo KYC. Procedi nella sezione KYC per completare i tuoi documenti.",

    // Events
    "nav.events": "Eventi",
    "events.overline": "Calendario riservato",
    "events.title": "Gli appuntamenti del club.",
    "events.intro": "Cene private, briefing macro, tavole rotonde. Tutti gli incontri richiedono richiesta di partecipazione, accolta dal team Compliance.",
    "events.locked_title": "Sezione riservata",
    "events.locked_body": "Il calendario eventi è visibile dopo il completamento del processo KYC. Procedi nella sezione KYC per sbloccarlo.",
    "events.section_upcoming": "Prossimi eventi",
    "events.section_past": "Eventi passati",
    "events.empty_upcoming": "Nessun evento in calendario al momento. Ti invieremo un invito appena sarà pubblicato.",
    "events.empty_past": "Ancora nessun evento passato da mostrare.",
    "events.starts_at": "Quando",
    "events.venue": "Dove",
    "events.dress_code": "Dress code",
    "events.capacity": "Posti",
    "events.rsvp_request": "Richiedi partecipazione",
    "events.rsvp_cancel": "Ritira richiesta",
    "events.rsvp_status_pending": "Richiesta inviata",
    "events.rsvp_status_accepted": "Partecipazione confermata",
    "events.rsvp_status_declined": "Richiesta non accolta",
    "events.rsvp_admin_comment": "Nota team Compliance",
    "events.rsvp_modal_title": "Richiesta di partecipazione",
    "events.rsvp_modal_intro": "La richiesta verrà valutata dal team Compliance, in base al tuo profilo e all'anzianità nel club. Riceverai conferma via email entro 2 giorni lavorativi.",
    "events.rsvp_modal_plusone": "Partecipo con un accompagnatore",
    "events.rsvp_modal_notes": "Note (vincoli alimentari, esigenze particolari)…",
    "events.rsvp_modal_submit": "Invia richiesta",
    "events.rsvp_modal_cancel": "Annulla",

    // Events — membership criteria card (informative only)
    "events.criteria_overline": "Criteri di partecipazione",
    "events.criteria_title": "Anzianità di club e accesso agli eventi.",
    "events.criteria_intro": "Le richieste di partecipazione sono valutate manualmente dal team Compliance in base all'anzianità nel club. Di seguito i criteri di riferimento.",
    "events.criteria_member": "Membro · accesso ai webinar e alle conferenze online",
    "events.criteria_senior": "Senior Member (3 anni) · accesso anche alle tavole rotonde",
    "events.criteria_legacy": "Legacy Member (5 anni) · accesso anche alle cene private",
    "events.criteria_patron": "Patron Member (7 anni) · accesso anche alle esperienze esclusive",
    "events.criteria_founders": "Founders Circle (10 anni) · priorità assoluta e invito agli eventi a posti limitati",
    "events.criteria_your_tenure": "La tua anzianità nel club è di {years} anni · tier attuale {tier}.",

    // Membership tier labels
    "membership.tier.member": "Membro",
    "membership.tier.senior_member": "Senior Member",
    "membership.tier.legacy_member": "Legacy Member",
    "membership.tier.patron_member": "Patron Member",
    "membership.tier.founders_circle": "Founders Circle",

    // Plan tier labels (Borealis ecosystem)
    "calc.tier_foundation": "Borealis Foundation",
    "calc.tier_prime": "Borealis Prime",
    "calc.tier_signature": "Borealis Signature",
    "calc.tier_sovereign": "Borealis Sovereign",
    "calc.tier_legacy": "Borealis Legacy",

    // Asset allocation slice labels

    // ---------- Introducer portal ----------
    "intro.nav_dashboard": "Cruscotto",
    "intro.nav_referrals": "Referral",
    "intro.nav_contract": "Contratto",
    "intro.nav_guide": "Manuale",
    "intro.contract_overline": "Contratto di segnalazione",
    "intro.contract_heading_a": "Il tuo contratto",
    "intro.contract_heading_em": "Borealis Reserve.",
    "intro.contract_intro": "Contratto di segnalazione e procacciamento d'affari. Firmalo digitalmente per iniziare a operare come Introducer autorizzato di Borealis Reserve.",
    "intro.contract_status_signed": "Firmato",
    "intro.contract_status_pending": "Da firmare",
    "intro.contract_signed_on": "Data firma",
    "intro.contract_download": "Scarica PDF",
    "intro.contract_sign_overline": "Sottoscrizione digitale",
    "intro.contract_sign_heading": "Firma il contratto",
    "intro.contract_sign_body": "Leggi il contratto qui sopra. Cliccando \"Firma digitalmente\" dichiari di aver letto e compreso tutti gli articoli. La firma è vincolante ai sensi dell'art. 20 CAD (D.Lgs. 82/2005) e produce effetti pari a quelli della scrittura privata (art. 2702 c.c.).",
    "intro.contract_ack_label": "Ho letto, compreso e accettato integralmente tutti gli articoli del contratto. Dichiaro di essere il titolare del profilo Introducer e procedo alla sottoscrizione digitale.",
    "intro.contract_sign_cta": "Firma digitalmente",
    "intro.contract_signing": "Firma in corso…",
    "intro.contract_legal_note": "IP, timestamp e hash tracciati",
    "intro.contract_signed_overline": "Sottoscritto",
    "intro.contract_signed_heading": "Contratto attivo.",
    "intro.contract_signed_body": "Copia PDF con firma, timestamp UTC, IP di sottoscrizione e hash SHA-256 di integrità ti è stata inviata via email. Il contratto è visibile e scaricabile qui in qualunque momento.",
    "intro.contract_back_dashboard": "Torna al cruscotto",
    "intro.contract_back": "Cruscotto Introducer",
    "intro.contract_banner_overline_pending": "Azione richiesta",
    "intro.contract_banner_title_pending": "Firma il contratto di segnalazione",
    "intro.contract_banner_body_pending": "Prima di iniziare l'attività di segnalazione devi sottoscrivere digitalmente il contratto. Bastano 60 secondi.",
    "intro.contract_banner_cta_pending": "Vai al contratto",
    "intro.contract_banner_overline_signed": "Contratto attivo",
    "intro.contract_banner_body_signed": "Sottoscritto il {date}. Copia PDF firmata inviata via email e disponibile sempre nell'area contratto.",
    "intro.contract_banner_view_signed": "Rivedi contratto",
    "intro.dashboard_overline": "Portale partner",
    "intro.dashboard_heading_a": "Benvenuto,",
    "intro.dashboard_intro": "Questa è la tua area riservata: qui vedi quante persone hai portato in Borealis Reserve, l'AUM aggregato e le commissioni maturate. La privacy dei clienti che porti è tutelata: non vedi né nomi né dati.",
    "intro.kpi_referrals_total": "Referral totali",
    "intro.kpi_referrals_active_sub": "{count} attivi",
    "intro.kpi_aum": "AUM portato",
    "intro.kpi_deposits_count": "{count} depositi attivi",
    "intro.kpi_accrued": "Maturato netto",
    "intro.kpi_accrued_sub": "Lordo: {gross}",
    "intro.kpi_yearly_estimate": "Stima annua netta",
    "intro.kpi_commission_rate": "{pct}% annuo sull'AUM",
    "intro.share_overline": "Il tuo link",
    "intro.share_heading": "Condividi Borealis Reserve.",
    "intro.share_body": "Ogni contatto arrivato tramite il link qui sotto verrà associato automaticamente al tuo codice promo.",
    "intro.copy_link": "Copia link",
    "intro.copied": "Copiato",
    "intro.code_label": "Codice",
    "intro.privacy_overline": "Privacy referral",
    "intro.privacy_body": "Per rispetto della riservatezza dei clienti che porti, non vedi mai nomi, email, telefoni o documenti KYC. Vedi solo aggregati anonimizzati (numero, AUM, tuo maturato). Il team Borealis gestisce direttamente ogni rapporto commerciale.",
    "intro.dashboard_view_statements": "Estratti mensili",
    "intro.dashboard_view_statements_sub": "Scarica i tuoi resoconti provvigionali",
    "intro.stmt_overline": "Provvigioni",
    "intro.stmt_heading_a": "Estratti",
    "intro.stmt_heading_em": "mensili.",
    "intro.stmt_intro": "Ogni mese, dopo la chiusura del periodo, il team Borealis pubblica qui il riepilogo delle provvigioni maturate: lordo, eventuale ritenuta d'acconto e netto liquidato. Il PDF è controfirmato e archiviato ai fini fiscali.",
    "intro.stmt_col_period": "Periodo",
    "intro.stmt_col_ref": "N. estratto",
    "intro.stmt_col_gross": "Lordo",
    "intro.stmt_col_withholding": "Ritenuta",
    "intro.stmt_col_net": "Netto",
    "intro.stmt_col_download": "Scarica",
    "intro.stmt_loading": "Caricamento…",
    "intro.stmt_empty": "Nessun estratto ancora pubblicato. Il primo estratto è disponibile a partire dal mese successivo alla firma del contratto.",
    "intro.stmt_footnote": "I dati fiscali sono elaborati secondo la normativa italiana applicabile (DPR 600/1973). Per chiarimenti scrivi a finance@borealisreserve.com.",
    "intro.dashboard_playbook": "Manuale Partner",
    "intro.dashboard_playbook_sub": "Guida passo-passo su come funziona la tua area riservata",
    "intro.dashboard_playbook_cta": "Apri manuale",
    "intro.referrals_overline": "I tuoi referral",
    "intro.referrals_heading_a": "Contatti",
    "intro.referrals_heading_em": "anonimizzati.",
    "intro.referrals_intro": "Elenco dei clienti che hai portato — solo indicatori aggregati. Nessun dato personale è mai esposto.",
    "intro.referrals_loading": "Caricamento…",
    "intro.referrals_empty": "Nessun referral ancora — condividi il tuo link o codice promo.",
    "intro.col_ref": "Referral",
    "intro.col_activated": "Attivato",
    "intro.col_status": "Stato",
    "intro.col_principal": "Contributo AUM",
    "intro.col_deposits": "Depositi",
    "intro.status_active": "Attivo",
    "intro.status_pending": "In attesa",
    "intro.status_suspended": "Sospeso",
    "intro.referrals_privacy_footnote": "* Le colonne sono aggregate al mese di attivazione per proteggere l'identità dei clienti. Il team Borealis può fornirti report certificati aggiuntivi su richiesta scritta.",

    // ---------- Public referral landing ----------
    "referral.brand_tagline": "Private Client Portal",
    "referral.checking": "Verifica del codice…",
    "referral.invalid_overline": "Link non valido",
    "referral.invalid_title": "Codice non riconosciuto",
    "referral.invalid_body": "Il codice promo non è più valido o è stato disattivato. Contatta chi ti ha condiviso il link per ricevere uno aggiornato.",
    "referral.back_to_login": "Vai al login",
    "referral.overline": "Sei stato presentato",
    "referral.heading_a": "Con la presentazione di",
    "referral.heading_b": "sei a un passo da Borealis Reserve.",
    "referral.intro": "Lasciaci i tuoi contatti: un consulente Borealis ti richiamerà per una prima conversazione riservata. Nessun impegno.",
    "referral.field_name": "Nome e cognome",
    "referral.field_email": "Email",
    "referral.field_phone": "Telefono",
    "referral.field_message": "Messaggio (facoltativo)",
    "referral.submitting": "Invio…",
    "referral.cta": "Invia richiesta",
    "referral.privacy_note": "I dati saranno trattati esclusivamente ai fini della valutazione del tuo interesse per Borealis Reserve, in conformità al Reg. UE 2016/679 (GDPR).",
    "referral.thanks_overline": "Grazie",
    "referral.thanks_title": "Richiesta ricevuta.",
    "referral.thanks_body": "Un consulente Borealis ti contatterà a breve. Se hai richieste urgenti, scrivi a info@borealisreserve.com.",
    "referral.err_required": "Nome e email sono obbligatori.",
    "referral.err_generic": "Si è verificato un errore. Riprova.",

  },
  en: {
    "switcher.label": "Language",

    "login.brand_tagline": "Private Client Portal",
    "login.overline": "Restricted access",
    "login.heading_a": "Your",
    "login.heading_em": "quietude",
    "login.heading_b": "awaits.",
    "login.intro": "Invited members only. No public registration.",
    "login.email": "Email",
    "login.password": "Password",
    "login.cta": "Enter the portal",
    "login.cta_loading": "Signing in…",
    "login.forgot": "Forgot password?",
    "login.no_invite_q": "Haven't received an invitation yet?",
    "login.no_invite_a": "Write to",
    "login.footer_brand": "Borealis Reserve · Private Club",
    "login.checking_session": "Verifying session…",

    "login.twofa_overline": "Two-factor authentication",
    "login.twofa_heading_a": "Enter the",
    "login.twofa_heading_em": "code",
    "login.twofa_heading_b": "from your Authenticator.",
    "login.twofa_intro": "Open your Authenticator app (Google Authenticator, Authy, 1Password) and type the 6-digit code.",
    "login.twofa_code_label": "6-digit code",
    "login.twofa_cta": "Verify and sign in",
    "login.twofa_back": "Back to login",

    "invite.brand_tagline": "Invite activation",
    "invite.overline": "Welcome",
    "invite.heading_em": "set",
    "invite.heading_lead": "your password.",
    "invite.intro": "You are activating the account for",
    "invite.field_name": "Full name",
    "invite.field_password": "New password",
    "invite.field_confirm": "Confirm password",
    "invite.cta": "Activate account and enter",
    "invite.cta_loading": "Activating…",
    "invite.checking": "Verifying invite…",
    "invite.error_title": "Invalid invitation",
    "invite.error_explainer": "Invitations are nominative, single-use, and valid for 7 days.",
    "invite.error_assistance": "For assistance write to",
    "invite.err_password_min": "Password must be at least 8 characters.",
    "invite.err_password_mismatch": "Passwords do not match.",
    "invite.greeting_named": "{name},",
    "invite.greeting_default": "Member,",

    "forgot.overline": "Absolute confidentiality",
    "forgot.title_a": "Password",
    "forgot.title_em": "recovery",
    "forgot.heading_a": "We do not handle resets",
    "forgot.heading_em": "online",
    "forgot.heading_b": ".",
    "forgot.p1": "To protect your capital, passwords are reset only through a verified channel with a member of our Reserve team.",
    "forgot.how_overline": "How to proceed",
    "forgot.step1": "Write from an email already known to your Relationship Manager",
    "forgot.step2": "You will receive a verification call within 24 hours",
    "forgot.step3": "Once verified, we issue a new single-use invitation",
    "forgot.cta_mail": "Write to clients@",
    "forgot.cta_compliance": "Compliance",
    "forgot.reminder_title": "Reminder",
    "forgot.reminder_body": "we never ask for passwords, OTPs or documents via email or phone before voice verification with your Relationship Manager.",
    "forgot.close": "Close",
    "forgot.form_email_label": "Account email",
    "forgot.form_submit": "Submit request",
    "forgot.form_submitting": "Sending…",
    "forgot.error_generic": "Something went wrong. Please try again later.",
    "forgot.confirmation_overline": "Request received",
    "forgot.confirmation_p1": "We received your request. A member of the Compliance team will contact you by phone within 24 hours to verify your identity.",
    "forgot.confirmation_p2": "If the email matches an active account you will receive a confirmation. Otherwise no email will be sent for security reasons.",

    "reset.overline": "Password recovery",
    "reset.title_a": "Set a new",
    "reset.title_em": "password",
    "reset.loading": "Validating link…",
    "reset.invalid_title": "Invalid link",
    "reset.invalid_expired": "This link has expired. Contact your Relationship Manager to receive a new one.",
    "reset.invalid_used": "This link has already been used. If this wasn't you, contact legal@borealisreserve.com immediately.",
    "reset.invalid_generic": "The link is not valid. Verify you copied the full URL from the email or request a new link.",
    "reset.back_to_login": "Back to sign-in",
    "reset.heading_a": "Choose a",
    "reset.heading_em": "new password",
    "reset.heading_b": ".",
    "reset.intro_for_email": "You are setting a new password for {email}.",
    "reset.new_password": "New password",
    "reset.confirm_password": "Confirm new password",
    "reset.policy_hint": "Minimum 10 characters, with at least one lowercase, one uppercase, one digit and one symbol.",
    "reset.mismatch": "The two passwords don't match.",
    "reset.submit": "Set new password",
    "reset.submitting": "Updating…",
    "reset.error_generic": "Something went wrong. Try again or contact your Relationship Manager.",
    "reset.done_title": "Password updated ✓",
    "reset.done_p1": "Your new password is active. For security reasons all previous sessions have been terminated: please sign in again.",
    "reset.go_login": "Go to sign-in",

    "change.overline": "Account security",
    "change.title_a": "Change",
    "change.title_em": "password",
    "change.heading_mandatory": "Set a personal password.",
    "change.heading_regular": "Change password.",
    "change.intro_mandatory": "You signed in with a temporary password provided by our team. To continue, choose a personal password.",
    "change.intro_regular": "Update your account password. After the change all active sessions will be terminated.",
    "change.current_label": "Current password (or temporary)",
    "change.new_label": "New password",
    "change.confirm_label": "Confirm new password",
    "change.policy_hint": "Minimum 10 characters, with at least one lowercase, one uppercase, one digit and one symbol.",
    "change.mismatch": "The two passwords don't match.",
    "change.same_as_current": "The new password must differ from the current one.",
    "change.submit": "Update password",
    "change.submitting": "Updating…",
    "change.error_generic": "Something went wrong. Please try again.",

    "nav.dashboard": "Dashboard",
    "nav.positions": "Deposits",
    "nav.statements": "Statements",
    "nav.logout": "Sign out",
    "pwa.install": "Install the app",
    "pwa.ios_title": "Install on iPhone / iPad",
    "pwa.ios_step1": "Tap the Share button in the Safari toolbar (the square with the upward arrow).",
    "pwa.ios_step2": "Scroll the list and choose “Add to Home Screen”, then confirm with “Add”.",
    "pwa.ios_note": "The Borealis Reserve app will appear on your Home Screen and open full-screen, like a native application.",
    "header.private_portal": "Private Portal",

    "footer.members_only": "© {year} Borealis Reserve · Members only",
    "footer.licenses_title": "Licences and registered offices",
    "footer.broker_title": "Brokerage Firm regulated by FINRA",
    "footer.broker_subline": "ABG Securities, Inc. · Finanshuset (USA) Securities, Inc. · FIBA Nordic Securities, Inc. · ABG Sundal Collier Inc.",
    "footer.main_address": "Main address",
    "footer.investment_firm": "Investment firm",
    "footer.registered_office": "Registered office",
    "footer.city": "City",
    "footer.country": "Country",
    "footer.enrolment_no": "Registry no.",
    "footer.finanstilsynet_id": "Finanstilsynet ID",
    "footer.finra_link": "FINRA profile →",
    "footer.consob_link": "Consob →",
    "footer.finanstilsynet_link": "Finanstilsynet →",
    "footer.legal_note": "Borealis Reserve is a service reserved to club members. Participation terms and yields shown in portal materials do not constitute a public offering or an investment solicitation under applicable regulations.",

    "dash.overline": "Welcome",
    "dash.greeting": "Good day, {name}.",
    "dash.greeting_default": "Member",
    "dash.intro": "Your capital works every second. Below is the real-time snapshot of your account.",
    "dash.loading": "Loading…",
    "dash.capital_managed": "Capital under management",
    "dash.on_n_deposits_one": "Across {n} active deposit.",
    "dash.on_n_deposits": "Across {n} active deposits.",
    "dash.updated_at": "Updated on {date}.",
    "dash.today_accrued": "Accrued today",
    "dash.linear_accrual": "Linear daily accrual",
    "dash.per_day": "{amount}/day",
    "dash.kpi_accrued": "Accrued to date",
    "dash.kpi_monthly": "Monthly estimate",
    "dash.kpi_yearly": "Yearly estimate",
    "dash.kpi_statements": "Statements available",
    "dash.kyc_overline": "Onboarding",
    "dash.kyc_title": "Complete your dossier",
    "dash.kyc_progress": "{approved} of {total} documents approved",
    "dash.kyc_submitted": "{n} under review",
    "dash.kyc_rejected": "{n} to re-upload",
    "dash.kyc_note": "Full access to plans, events and the team unlocks once your KYC is complete.",
    "dash.kyc_cta": "Complete now",
    "dash.next_coupon": "Next coupon",
    "dash.principal": "Principal",
    "dash.coupon_amount": "Coupon amount",
    "dash.your_deposits": "Your deposits",
    "dash.see_all": "View all →",
    "dash.net_rate": "Net rate",
    "dash.accrued": "Accrued",
    "dash.daily": "Per day",
    "dash.auto_reinvest_active": "Auto-reinvest active",
    "dash.deposit": "Deposit",
    "dash.days_progress": "{elapsed}/{total} d",

    "pos.overline": "Positions",
    "pos.heading_a": "Your",
    "pos.heading_em": "deposits",
    "pos.heading_b": ".",
    "pos.intro": "Linear daily accrual, capitalisation only if &quot;Auto-reinvest&quot; is active at maturity.",
    "pos.col_deposit": "Deposit",
    "pos.col_principal": "Principal",
    "pos.col_gross_rate": "Gross rate",
    "pos.col_fee": "Fee",
    "pos.col_net_rate": "Net rate",
    "pos.col_start": "Start date",
    "pos.col_end": "Maturity",
    "pos.col_accrued": "Net accrued",
    "pos.col_daily": "/Day",
    "pos.col_maturity_value": "Value at maturity",
    "pos.auto_reinvest_short": "Auto-reinvest",
    "pos.empty": "No active deposits yet.",

    "stmt.overline": "Documents",
    "stmt.heading_a": "Statements and",
    "stmt.heading_em": "certifications",
    "stmt.heading_b": ".",
    "stmt.intro": "Published by the back office. Download signed PDFs for your accounting or accountant.",
    "stmt.col_period": "Period",
    "stmt.col_kind": "Type",
    "stmt.col_label": "Label",
    "stmt.col_published": "Published",
    "stmt.col_actions": "Actions",
    "stmt.kind_quarterly": "Quarterly",
    "stmt.kind_monthly": "Monthly",
    "stmt.kind_annual": "Annual",
    "stmt.kind_coupon": "Coupon",
    "stmt.label_default": "Statement",
    "stmt.download": "Download",
    "stmt.empty": "No statements available yet.",

    "bank.overline": "Bank details",
    "bank.title": "Where to wire your capital",
    "bank.intro": "Wire transfers must only be made to the coordinates below, dedicated to your account.",
    "bank.empty_title": "Awaiting assignment",
    "bank.empty_body": "Your Relationship Manager is finalising the bank details dedicated to your account. You will be notified as soon as they are available.",
    "bank.beneficiary": "Beneficiary",
    "bank.iban": "IBAN",
    "bank.bic": "BIC / SWIFT",
    "bank.bank": "Bank",
    "bank.reference": "Suggested reference",
    "bank.copy": "Copy",
    "bank.copied": "Copied",
    "bank.disclaimer": "Always verify these coordinates with your advisor before initiating a transfer. Borealis Reserve never requests IBAN changes via email.",

    "nav.explore": "Explore",
    "explore.philosophy": "Philosophy",
    "explore.compliance": "Compliance",
    "explore.faq": "FAQ",
    "explore.overline": "Reserved knowledge",
    "explore.title": "The club, in detail.",
    "explore.intro": "Three internal rooms dedicated to members: our operating philosophy, the legal & supervisory structure and the most frequent questions before investing.",
    "explore.card_philosophy_desc": "A Scandinavian model, applied to a Mediterranean client. How Borealis works in four steps.",
    "explore.card_compliance_desc": "Legal structure, supervisory authority, deposit guarantees, conflicts of interest, GDPR.",
    "explore.card_faq_desc": "Six questions before investing. Direct answers, no small asterisk.",
    "explore.read": "Read more →",
    "explore.back": "← Back to Explore",

    "nav.kyc": "KYC",
    "kyc.overline": "KYC onboarding",
    "kyc.title": "Your documents, in order.",
    "kyc.intro": "To fully activate your Borealis Reserve account we need you to sign a few documents and upload the others. A one-off operation, calmly.",
    "kyc.progress_label": "Progress",
    "kyc.progress_value": "{approved} of {total} approved",
    "kyc.complete_banner": "Onboarding complete. Your account is fully operational.",
    "kyc.status_pending": "To complete",
    "kyc.status_submitted": "Under review",
    "kyc.status_approved": "Approved",
    "kyc.status_rejected": "Needs review",
    "kyc.kind_sign": "Digital signature",
    "kyc.kind_upload": "Document upload",
    "kyc.action_view_template": "Open template",
    "kyc.action_download_template": "Download template",
    "kyc.action_sign": "Sign digitally",
    "kyc.action_resign": "Sign again",
    "kyc.action_upload": "Upload file",
    "kyc.action_replace": "Replace",
    "kyc.action_download_signed": "Download signed copy",
    "kyc.action_download_uploaded": "View uploaded document",
    "kyc.upload_accept": "PDF, JPG, PNG · max 12 MB",
    "kyc.uploading": "Uploading…",
    "kyc.signing": "Signing…",
    "kyc.last_comment": "Compliance note",
    "kyc.sign_modal_title": "Simple electronic signature",
    "kyc.sign_modal_intro": "You are about to apply your electronic signature to the document <strong>{title}</strong>. The signature will be recorded with audit trail (UTC timestamp, IP, SHA-256 hash) pursuant to EU Regulation 910/2014 (eIDAS).",
    "kyc.sign_modal_checkbox": "I declare that I have read, understood and fully accepted the content of the document.",
    "kyc.sign_modal_confirm": "Sign now",
    "kyc.sign_modal_cancel": "Cancel",
    "kyc.sign_modal_disclaimer": "Simple electronic signature produces the legal effects of a written subscription between the parties.",

    "calc.overline": "Yield simulator",
    "calc.title": "Estimate your horizon.",
    "calc.intro": "Move the slider to simulate your expected yield over the first 12 months of capital tenure. Values reflect the current packages published by the Borealis Reserve team.",
    "calc.amount_label": "Capital to contribute",
    "calc.duration_label": "Tenure",
    "calc.duration_12": "12 months",
    "calc.duration_24": "24 months",
    "calc.duration_36": "36 months",
    "calc.duration_open": "Open-ended",
    "calc.tier_label": "Applicable package",
    "calc.rate_gross": "Expected gross yield",
    "calc.rate_net": "Estimated annual net yield",
    "calc.mngmt_fee": "Management fee",
    "calc.perf_fee": "Performance fee",
    "calc.lock_period": "Lock period",
    "calc.gross_profit": "Gross profit ({months}m)",
    "calc.net_profit": "Net profit ({months}m)",
    "calc.maturity_value": "Maturity value",
    "calc.chart_title": "Capital growth",
    "calc.chart_legend": "{months}-month projection",
    "calc.chart_open_note": "Indicative 12-month projection",
    "calc.no_plan": "No package is currently active for this capital. Contact your Relationship Manager.",
    "calc.disclaimer": "Non-binding simulation for illustration purposes. Actual conditions are those of the package subscribed at the time of contribution.",
    "disc.title": "Regulatory information",
    "disc.past": "Past performance is not a reliable indicator of future results.",
    "disc.risk": "Your capital is at risk. The value of investments can fall as well as rise.",
    "disc.mkt": "Marketing communication. This does not constitute financial advice or a public offer.",
    "nav.plans": "Plans",
    "plans.overline": "Subscription",
    "plans.title": "Investment plans",
    "plans.intro": "Explore the members-only fixed-yield packages. Select a plan to send a subscription request to the Finance & Compliance team.",
    "plans.loading": "Loading plans…",
    "plans.kyc_title": "Explore freely",
    "plans.kyc_body": "Browse the packages, download the KIDs and simulate returns. You can submit your subscription request right away: to activate it we will ask you to complete KYC verification.",
    "plans.kyc_cta": "Go to KYC",
    "plans.partner_title": "Partner mode",
    "plans.partner_body": "Presentation showcase: use the product sheets, yield simulator and KIDs to introduce Borealis Reserve to your contacts. Subscribing to plans is reserved for actual clients.",
    "plans.partner_showcase_badge": "Presentation only",
    "plans.partner_your_code": "Your code",
    "plans.partner_copy_link": "Copy your link →",
    "plans.range": "Contribution range",
    "plans.checkbox": "I have read the information and wish to request this plan",
    "plans.request_cta": "Request subscription",
    "plans.pending_badge": "Request pending",
    "plans.amount_label": "Indicative amount (optional)",
    "plans.amount_help": "Within the plan range. The final amount will be agreed with the team.",
    "plans.note_label": "Note for the team (optional)",
    "plans.modal_title": "Confirm subscription request",
    "plans.modal_body": "By clicking “Confirm and send”, your request for the “{plan}” plan will be sent to the Finance & Compliance team. You will receive further details by email and phone, including — if accepted — the wire transfer coordinates. No payment is due at this stage.",
    "plans.modal_confirm": "Confirm and send",
    "plans.modal_cancel": "Cancel",
    "plans.toast_ok": "Request sent. The Finance & Compliance team will contact you shortly.",
    "plans.toast_err": "Could not send the request. Please try again.",
    "plans.my_requests": "My requests",
    "plans.status_pending": "Pending",
    "plans.status_awaiting_kyc": "Awaiting KYC",
    "plans.toast_ok_kyc": "Request submitted. Now complete your KYC verification: once approved, your request will move to the Investments team.",
    "plans.status_approved": "Approved",
    "plans.status_rejected": "Not accepted",
    "plans.no_requests": "You have not submitted any requests yet.",
    "plans.kid_download": "Download KID (PDF)",
    "plans.kid_ack_label": "I have read and understood the Key Information Document (KID) for this product",
    "plans.kid_modal_hint": "Before submitting your request, please review the product's Key Information Document (KID).",
    "plans.allocation_title": "Asset allocation",
    "plans.volatility_target": "Volatility target",
    "plans.allocation_show": "View asset allocation",
    "plans.allocation_hide": "Hide asset allocation",
    "plans.allocation_note": "Indicative target allocation for illustration, rebalanced periodically by the Borealis Investment Committee. Actual weightings may vary with market conditions.",
    "alloc.global_equity_etf": "Global Equity ETFs",
    "alloc.global_equity": "Global Equity",
    "alloc.investment_grade_bonds": "Investment Grade Bonds",
    "alloc.money_market_short": "Money Market & Short Duration",
    "alloc.money_market": "Money Market",
    "alloc.alternative_ucits_funds": "Alternative UCITS Funds",
    "alloc.alternative_ucits_strategies": "Alternative UCITS Strategies",
    "alloc.alternative_strategies": "Alternative Strategies",
    "alloc.gold_commodities": "Gold & Commodities",
    "alloc.commodities": "Commodities",
    "alloc.private_markets": "Private Markets",
    "alloc.private_equity": "Private Equity",
    "alloc.private_credit": "Private Credit",
    "alloc.real_assets": "Real Assets",
    "alloc.cash": "Cash",
    "alloc.tactical_cash": "Tactical Cash",
    "calc.months": "months",
    "calc.lock_open": "Open-ended",

    "team.overline": "Management team",
    "team.title": "The people behind your capital.",
    "team.intro": "The professionals structuring your strategy. Regulated profiles, verifiable on FINRA.",
    "team.crd": "CRD#",
    "team.years_experience": "years of experience",
    "team.finra_link": "FINRA profile →",
    "team.locked_title": "Reserved section",
    "team.locked_body": "The management team will become visible after the KYC process is completed. Proceed to the KYC section to complete your documents.",

    "nav.events": "Events",
    "events.overline": "Reserved calendar",
    "events.title": "The club's appointments.",
    "events.intro": "Private dinners, macro briefings, roundtables. All gatherings require a participation request, approved by the Compliance team.",
    "events.locked_title": "Reserved section",
    "events.locked_body": "The events calendar becomes visible after the KYC process is completed. Proceed to the KYC section to unlock it.",
    "events.section_upcoming": "Upcoming events",
    "events.section_past": "Past events",
    "events.empty_upcoming": "No events on the calendar at the moment. We will send you an invitation as soon as one is published.",
    "events.empty_past": "No past events to show yet.",
    "events.starts_at": "When",
    "events.venue": "Where",
    "events.dress_code": "Dress code",
    "events.capacity": "Seats",
    "events.rsvp_request": "Request participation",
    "events.rsvp_cancel": "Withdraw request",
    "events.rsvp_status_pending": "Request sent",
    "events.rsvp_status_accepted": "Participation confirmed",
    "events.rsvp_status_declined": "Request not accepted",
    "events.rsvp_admin_comment": "Compliance team note",
    "events.rsvp_modal_title": "Participation request",
    "events.rsvp_modal_intro": "The request will be evaluated by the Compliance team, based on your profile and tenure in the club. You will receive confirmation via email within 2 business days.",
    "events.rsvp_modal_plusone": "I will attend with a guest",
    "events.rsvp_modal_notes": "Notes (dietary requirements, special needs)…",
    "events.rsvp_modal_submit": "Submit request",
    "events.rsvp_modal_cancel": "Cancel",

    "events.criteria_overline": "Participation criteria",
    "events.criteria_title": "Club tenure and event access.",
    "events.criteria_intro": "Participation requests are reviewed manually by the Compliance team based on club tenure. The reference criteria are listed below.",
    "events.criteria_member": "Member · access to webinars and online conferences",
    "events.criteria_senior": "Senior Member (3 years) · also access to roundtables",
    "events.criteria_legacy": "Legacy Member (5 years) · also access to private dinners",
    "events.criteria_patron": "Patron Member (7 years) · also access to exclusive experiences",
    "events.criteria_founders": "Founders Circle (10 years) · absolute priority and invitations to seat-limited events",
    "events.criteria_your_tenure": "Your club tenure is {years} years · current tier {tier}.",

    "membership.tier.member": "Member",
    "membership.tier.senior_member": "Senior Member",
    "membership.tier.legacy_member": "Legacy Member",
    "membership.tier.patron_member": "Patron Member",
    "membership.tier.founders_circle": "Founders Circle",

    "calc.tier_foundation": "Borealis Foundation",
    "calc.tier_prime": "Borealis Prime",
    "calc.tier_signature": "Borealis Signature",
    "calc.tier_sovereign": "Borealis Sovereign",
    "calc.tier_legacy": "Borealis Legacy",

    // ---------- Introducer portal ----------
    "intro.nav_dashboard": "Dashboard",
    "intro.nav_referrals": "Referrals",
    "intro.nav_contract": "Contract",
    "intro.nav_guide": "Playbook",
    "intro.contract_overline": "Intermediation contract",
    "intro.contract_heading_a": "Your contract",
    "intro.contract_heading_em": "with Borealis Reserve.",
    "intro.contract_intro": "Referral & intermediation agreement. Sign it digitally to begin operating as an authorised Borealis Reserve Introducer.",
    "intro.contract_status_signed": "Signed",
    "intro.contract_status_pending": "To be signed",
    "intro.contract_signed_on": "Signed on",
    "intro.contract_download": "Download PDF",
    "intro.contract_sign_overline": "Digital signature",
    "intro.contract_sign_heading": "Sign the contract",
    "intro.contract_sign_body": "Please read the contract above. Clicking \"Sign digitally\" confirms that you have read and understood all clauses. The signature is legally binding under Italian art. 20 CAD (D.Lgs. 82/2005) and produces the same effect as a written signature (art. 2702 c.c.).",
    "intro.contract_ack_label": "I have read, understood and accept in full all articles of the contract. I am the account holder and proceed with digital signature.",
    "intro.contract_sign_cta": "Sign digitally",
    "intro.contract_signing": "Signing…",
    "intro.contract_legal_note": "IP, timestamp and hash logged",
    "intro.contract_signed_overline": "Signed",
    "intro.contract_signed_heading": "Contract active.",
    "intro.contract_signed_body": "A signed copy with timestamp, source IP and SHA-256 integrity hash has been emailed to you. You can always review and download it from here.",
    "intro.contract_back_dashboard": "Back to dashboard",
    "intro.contract_back": "Introducer dashboard",
    "intro.contract_banner_overline_pending": "Action required",
    "intro.contract_banner_title_pending": "Sign the referral contract",
    "intro.contract_banner_body_pending": "Before starting referral activity you must sign the contract digitally. Takes 60 seconds.",
    "intro.contract_banner_cta_pending": "Go to contract",
    "intro.contract_banner_overline_signed": "Contract active",
    "intro.contract_banner_body_signed": "Signed on {date}. Signed PDF emailed and always available in the contract area.",
    "intro.contract_banner_view_signed": "Review contract",
    "intro.dashboard_overline": "Partner portal",
    "intro.dashboard_heading_a": "Welcome,",
    "intro.dashboard_intro": "Your private area — see how many contacts you introduced to Borealis Reserve, the aggregate AUM and the commissions you accrued. Referred clients' privacy is protected: names and details are never exposed.",
    "intro.kpi_referrals_total": "Total referrals",
    "intro.kpi_referrals_active_sub": "{count} active",
    "intro.kpi_aum": "AUM introduced",
    "intro.kpi_deposits_count": "{count} active deposits",
    "intro.kpi_accrued": "Accrued (net)",
    "intro.kpi_accrued_sub": "Gross: {gross}",
    "intro.kpi_yearly_estimate": "Yearly estimate (net)",
    "intro.kpi_commission_rate": "{pct}% p.a. on AUM",
    "intro.share_overline": "Your link",
    "intro.share_heading": "Share Borealis Reserve.",
    "intro.share_body": "Every contact arriving through the link below is automatically tied to your promo code.",
    "intro.copy_link": "Copy link",
    "intro.copied": "Copied",
    "intro.code_label": "Code",
    "intro.privacy_overline": "Referral privacy",
    "intro.privacy_body": "To respect the confidentiality of the clients you introduce, you never see names, emails, phones or KYC documents. You only see anonymised aggregates (count, AUM, your accrued commission). Borealis manages every commercial relationship directly.",
    "intro.dashboard_view_statements": "Monthly statements",
    "intro.dashboard_view_statements_sub": "Download your commission reports",
    "intro.stmt_overline": "Commissions",
    "intro.stmt_heading_a": "Monthly",
    "intro.stmt_heading_em": "statements.",
    "intro.stmt_intro": "At the end of every month the Borealis team publishes your commission report here: gross amount, any withholding tax and the net paid out. Each PDF is countersigned and archived for tax purposes.",
    "intro.stmt_col_period": "Period",
    "intro.stmt_col_ref": "Statement no.",
    "intro.stmt_col_gross": "Gross",
    "intro.stmt_col_withholding": "Withholding",
    "intro.stmt_col_net": "Net",
    "intro.stmt_col_download": "Download",
    "intro.stmt_loading": "Loading…",
    "intro.stmt_empty": "No statement published yet. The first one is available from the month after your contract is signed.",
    "intro.stmt_footnote": "Tax data are processed under the applicable regulation. For any question write to finance@borealisreserve.com.",
    "intro.dashboard_playbook": "Partner Playbook",
    "intro.dashboard_playbook_sub": "Step-by-step guide to your Partner portal",
    "intro.dashboard_playbook_cta": "Open playbook",
    "intro.referrals_overline": "Your referrals",
    "intro.referrals_heading_a": "Contacts",
    "intro.referrals_heading_em": "anonymised.",
    "intro.referrals_intro": "The clients you introduced — aggregate indicators only. No personal data is ever exposed.",
    "intro.referrals_loading": "Loading…",
    "intro.referrals_empty": "No referrals yet — share your link or promo code.",
    "intro.col_ref": "Referral",
    "intro.col_activated": "Activated",
    "intro.col_status": "Status",
    "intro.col_principal": "AUM contribution",
    "intro.col_deposits": "Deposits",
    "intro.status_active": "Active",
    "intro.status_pending": "Pending",
    "intro.status_suspended": "Suspended",
    "intro.referrals_privacy_footnote": "* Columns are rounded to the activation month to protect client identity. Borealis can provide additional certified reports upon written request.",

    // ---------- Public referral landing ----------
    "referral.brand_tagline": "Private Client Portal",
    "referral.checking": "Verifying code…",
    "referral.invalid_overline": "Invalid link",
    "referral.invalid_title": "Code not recognised",
    "referral.invalid_body": "The promo code is no longer valid or has been disabled. Please contact whoever shared the link with you to receive an updated one.",
    "referral.back_to_login": "Go to login",
    "referral.overline": "You've been introduced",
    "referral.heading_a": "Introduced by",
    "referral.heading_b": ", you're one step away from Borealis Reserve.",
    "referral.intro": "Leave us your details: a Borealis advisor will get in touch for a discreet first conversation. No commitment.",
    "referral.field_name": "Full name",
    "referral.field_email": "Email",
    "referral.field_phone": "Phone",
    "referral.field_message": "Message (optional)",
    "referral.submitting": "Sending…",
    "referral.cta": "Send request",
    "referral.privacy_note": "Your data will be processed solely to evaluate your interest in Borealis Reserve, in accordance with EU Reg. 2016/679 (GDPR).",
    "referral.thanks_overline": "Thank you",
    "referral.thanks_title": "Request received.",
    "referral.thanks_body": "A Borealis advisor will reach out shortly. For urgent matters, write to info@borealisreserve.com.",
    "referral.err_required": "Name and email are required.",
    "referral.err_generic": "Something went wrong. Please try again.",

  },
  no: {
    "switcher.label": "Språk",

    "login.brand_tagline": "Privat klientportal",
    "login.overline": "Begrenset tilgang",
    "login.heading_a": "Din",
    "login.heading_em": "ro",
    "login.heading_b": "venter på deg.",
    "login.intro": "Kun for inviterte medlemmer. Ingen offentlig registrering.",
    "login.email": "E-post",
    "login.password": "Passord",
    "login.cta": "Logg inn på portalen",
    "login.cta_loading": "Logger inn…",
    "login.forgot": "Glemt passord?",
    "login.no_invite_q": "Har du ikke mottatt en invitasjon ennå?",
    "login.no_invite_a": "Skriv til",
    "login.footer_brand": "Borealis Reserve · Privat klubb",
    "login.checking_session": "Sjekker økt…",

    "login.twofa_overline": "To-faktor autentisering",
    "login.twofa_heading_a": "Skriv inn",
    "login.twofa_heading_em": "koden",
    "login.twofa_heading_b": "fra Authenticator-appen din.",
    "login.twofa_intro": "Åpne Authenticator-appen (Google Authenticator, Authy, 1Password) og skriv inn 6-sifret kode.",
    "login.twofa_code_label": "6-sifret kode",
    "login.twofa_cta": "Verifiser og logg inn",
    "login.twofa_back": "Tilbake til innlogging",

    "invite.brand_tagline": "Invitasjonsaktivering",
    "invite.overline": "Velkommen",
    "invite.heading_em": "sett",
    "invite.heading_lead": "passordet ditt.",
    "invite.intro": "Du aktiverer kontoen for",
    "invite.field_name": "Fullt navn",
    "invite.field_password": "Nytt passord",
    "invite.field_confirm": "Bekreft passord",
    "invite.cta": "Aktiver konto og logg inn",
    "invite.cta_loading": "Aktiverer…",
    "invite.checking": "Sjekker invitasjon…",
    "invite.error_title": "Ugyldig invitasjon",
    "invite.error_explainer": "Invitasjoner er personlige, til engangsbruk, og gyldige i 7 dager.",
    "invite.error_assistance": "For hjelp skriv til",
    "invite.err_password_min": "Passordet må være minst 8 tegn.",
    "invite.err_password_mismatch": "Passordene stemmer ikke overens.",
    "invite.greeting_named": "{name},",
    "invite.greeting_default": "Medlem,",

    "forgot.overline": "Absolutt konfidensialitet",
    "forgot.title_a": "Passord",
    "forgot.title_em": "gjenoppretting",
    "forgot.heading_a": "Vi håndterer ikke tilbakestillinger",
    "forgot.heading_em": "på nett",
    "forgot.heading_b": ".",
    "forgot.p1": "For å beskytte kapitalen din, tilbakestilles passord kun via en verifisert kanal med et medlem av vårt Reserve-team.",
    "forgot.how_overline": "Slik går du frem",
    "forgot.step1": "Skriv fra en e-post Relationship Manager-en din allerede kjenner",
    "forgot.step2": "Du vil motta en verifiseringssamtale innen 24 timer",
    "forgot.step3": "Når det er verifisert, utsteder vi en ny engangsinvitasjon",
    "forgot.cta_mail": "Skriv til clients@",
    "forgot.cta_compliance": "Compliance",
    "forgot.reminder_title": "Påminnelse",
    "forgot.reminder_body": "vi spør aldri om passord, OTP eller dokumenter via e-post eller telefon før talverifisering med Relationship Manager-en din.",
    "forgot.close": "Lukk",
    "forgot.form_email_label": "Konto-e-postadresse",
    "forgot.form_submit": "Send forespørsel",
    "forgot.form_submitting": "Sender…",
    "forgot.error_generic": "Noe gikk galt. Vennligst prøv igjen senere.",
    "forgot.confirmation_overline": "Forespørsel mottatt",
    "forgot.confirmation_p1": "Vi har mottatt forespørselen din. Et medlem av Compliance-teamet vil kontakte deg per telefon innen 24 timer for å verifisere identiteten din.",
    "forgot.confirmation_p2": "Hvis e-posten samsvarer med en aktiv konto vil du motta en bekreftelse. Ellers blir ingen e-post sendt av sikkerhetshensyn.",

    "reset.overline": "Gjenoppretting av passord",
    "reset.title_a": "Sett nytt",
    "reset.title_em": "passord",
    "reset.loading": "Validerer lenken…",
    "reset.invalid_title": "Ugyldig lenke",
    "reset.invalid_expired": "Denne lenken har utløpt. Kontakt Relationship Manager-en din for å få en ny.",
    "reset.invalid_used": "Denne lenken er allerede brukt. Hvis det ikke var deg, kontakt legal@borealisreserve.com umiddelbart.",
    "reset.invalid_generic": "Lenken er ikke gyldig. Sjekk at du har kopiert hele URL-en fra e-posten, eller be om en ny lenke.",
    "reset.back_to_login": "Tilbake til innlogging",
    "reset.heading_a": "Velg et",
    "reset.heading_em": "nytt passord",
    "reset.heading_b": ".",
    "reset.intro_for_email": "Du setter et nytt passord for {email}.",
    "reset.new_password": "Nytt passord",
    "reset.confirm_password": "Bekreft nytt passord",
    "reset.policy_hint": "Minimum 10 tegn, med minst én liten bokstav, én stor bokstav, ett tall og ett symbol.",
    "reset.mismatch": "De to passordene stemmer ikke overens.",
    "reset.submit": "Sett nytt passord",
    "reset.submitting": "Oppdaterer…",
    "reset.error_generic": "Noe gikk galt. Prøv igjen eller kontakt Relationship Manager-en din.",
    "reset.done_title": "Passord oppdatert ✓",
    "reset.done_p1": "Det nye passordet ditt er aktivt. Av sikkerhetshensyn er alle tidligere økter avsluttet: logg inn på nytt.",
    "reset.go_login": "Gå til innlogging",

    "change.overline": "Kontosikkerhet",
    "change.title_a": "Bytt",
    "change.title_em": "passord",
    "change.heading_mandatory": "Sett et personlig passord.",
    "change.heading_regular": "Endre passord.",
    "change.intro_mandatory": "Du logget inn med et midlertidig passord fra teamet vårt. For å fortsette, velg et personlig passord.",
    "change.intro_regular": "Oppdater kontopassordet ditt. Etter endringen vil alle aktive økter avsluttes.",
    "change.current_label": "Nåværende passord (eller midlertidig)",
    "change.new_label": "Nytt passord",
    "change.confirm_label": "Bekreft nytt passord",
    "change.policy_hint": "Minimum 10 tegn, med minst én liten bokstav, én stor bokstav, ett tall og ett symbol.",
    "change.mismatch": "De to passordene stemmer ikke overens.",
    "change.same_as_current": "Det nye passordet må være forskjellig fra det nåværende.",
    "change.submit": "Oppdater passord",
    "change.submitting": "Oppdaterer…",
    "change.error_generic": "Noe gikk galt. Prøv igjen.",

    "nav.dashboard": "Dashbord",
    "nav.positions": "Innskudd",
    "nav.statements": "Kontoutskrifter",
    "nav.logout": "Logg ut",
    "pwa.install": "Installer appen",
    "pwa.ios_title": "Installer på iPhone / iPad",
    "pwa.ios_step1": "Trykk på Del-knappen i Safari-verktøylinjen (firkanten med pil opp).",
    "pwa.ios_step2": "Bla i listen og velg «Legg til på Hjem-skjerm», og bekreft med «Legg til».",
    "pwa.ios_note": "Borealis Reserve-appen vises på Hjem-skjermen og åpnes i fullskjerm, som en vanlig app.",
    "header.private_portal": "Privatportal",

    "footer.members_only": "© {year} Borealis Reserve · Kun for medlemmer",
    "footer.licenses_title": "Lisenser og hovedkontorer",
    "footer.broker_title": "Brokerage Firm regulert av FINRA",
    "footer.broker_subline": "ABG Securities, Inc. · Finanshuset (USA) Securities, Inc. · FIBA Nordic Securities, Inc. · ABG Sundal Collier Inc.",
    "footer.main_address": "Hovedadresse",
    "footer.investment_firm": "Verdipapirforetak",
    "footer.registered_office": "Forretningsadresse",
    "footer.city": "By",
    "footer.country": "Land",
    "footer.enrolment_no": "Registernr.",
    "footer.finanstilsynet_id": "Finanstilsynet-ID",
    "footer.finra_link": "FINRA-profil →",
    "footer.consob_link": "Consob →",
    "footer.finanstilsynet_link": "Finanstilsynet →",
    "footer.legal_note": "Borealis Reserve er en tjeneste forbeholdt klubbens medlemmer. Deltakelsesvilkår og avkastning vist i portalens materiell utgjør verken et offentlig tilbud eller en investeringsoppfordring etter gjeldende regelverk.",

    "dash.overline": "Velkommen",
    "dash.greeting": "God dag, {name}.",
    "dash.greeting_default": "Medlem",
    "dash.intro": "Kapitalen din arbeider hvert sekund. Nedenfor finner du sanntidsoversikt over kontoen din.",
    "dash.loading": "Laster…",
    "dash.capital_managed": "Forvaltet kapital",
    "dash.on_n_deposits_one": "På {n} aktivt innskudd.",
    "dash.on_n_deposits": "På {n} aktive innskudd.",
    "dash.updated_at": "Oppdatert {date}.",
    "dash.today_accrued": "Påløpt i dag",
    "dash.linear_accrual": "Lineær daglig påløping",
    "dash.per_day": "{amount}/dag",
    "dash.kpi_accrued": "Påløpt til dags dato",
    "dash.kpi_monthly": "Månedlig estimat",
    "dash.kpi_yearly": "Årlig estimat",
    "dash.kpi_statements": "Tilgjengelige utskrifter",
    "dash.kyc_overline": "Onboarding",
    "dash.kyc_title": "Fullfør dokumentmappen din",
    "dash.kyc_progress": "{approved} av {total} dokumenter godkjent",
    "dash.kyc_submitted": "{n} under vurdering",
    "dash.kyc_rejected": "{n} må lastes opp på nytt",
    "dash.kyc_note": "Full tilgang til planer, arrangementer og teamet låses opp når KYC er fullført.",
    "dash.kyc_cta": "Fullfør nå",
    "dash.next_coupon": "Neste kupong",
    "dash.principal": "Hovedstol",
    "dash.coupon_amount": "Kupongbeløp",
    "dash.your_deposits": "Innskuddene dine",
    "dash.see_all": "Se alle →",
    "dash.net_rate": "Netto rente",
    "dash.accrued": "Påløpt",
    "dash.daily": "Per dag",
    "dash.auto_reinvest_active": "Auto-reinvestering aktiv",
    "dash.deposit": "Innskudd",
    "dash.days_progress": "{elapsed}/{total} d",

    "pos.overline": "Posisjoner",
    "pos.heading_a": "Dine",
    "pos.heading_em": "innskudd",
    "pos.heading_b": ".",
    "pos.intro": "Lineær daglig påløping, kapitalisering kun hvis &quot;Auto-reinvestering&quot; er aktiv ved forfall.",
    "pos.col_deposit": "Innskudd",
    "pos.col_principal": "Hovedstol",
    "pos.col_gross_rate": "Brutto rente",
    "pos.col_fee": "Gebyr",
    "pos.col_net_rate": "Netto rente",
    "pos.col_start": "Startdato",
    "pos.col_end": "Forfall",
    "pos.col_accrued": "Netto påløpt",
    "pos.col_daily": "/Dag",
    "pos.col_maturity_value": "Verdi ved forfall",
    "pos.auto_reinvest_short": "Auto-reinvest",
    "pos.empty": "Ingen aktive innskudd ennå.",

    "stmt.overline": "Dokumenter",
    "stmt.heading_a": "Kontoutskrifter og",
    "stmt.heading_em": "sertifiseringer",
    "stmt.heading_b": ".",
    "stmt.intro": "Publisert av back office. Last ned signerte PDF-er for regnskapet ditt eller revisoren.",
    "stmt.col_period": "Periode",
    "stmt.col_kind": "Type",
    "stmt.col_label": "Etikett",
    "stmt.col_published": "Publisert",
    "stmt.col_actions": "Handlinger",
    "stmt.kind_quarterly": "Kvartalsvis",
    "stmt.kind_monthly": "Månedlig",
    "stmt.kind_annual": "Årlig",
    "stmt.kind_coupon": "Kupong",
    "stmt.label_default": "Kontoutskrift",
    "stmt.download": "Last ned",
    "stmt.empty": "Ingen utskrifter tilgjengelig ennå.",

    "bank.overline": "Bankkoordinater",
    "bank.title": "Hvor du skal overføre kapitalen",
    "bank.intro": "Bankoverføringer skal kun gjøres til koordinatene nedenfor, dedikert til kontoen din.",
    "bank.empty_title": "Venter på tildeling",
    "bank.empty_body": "Din Relationship Manager fullfører bankkoordinatene som er dedikert til kontoen din. Du vil bli varslet så snart de er tilgjengelige.",
    "bank.beneficiary": "Mottaker",
    "bank.iban": "IBAN",
    "bank.bic": "BIC / SWIFT",
    "bank.bank": "Bank",
    "bank.reference": "Foreslått referanse",
    "bank.copy": "Kopier",
    "bank.copied": "Kopiert",
    "bank.disclaimer": "Verifiser alltid disse koordinatene med rådgiveren din før du initierer en overføring. Borealis Reserve ber aldri om IBAN-endringer via e-post.",

    "nav.explore": "Utforsk",
    "explore.philosophy": "Filosofi",
    "explore.compliance": "Compliance",
    "explore.faq": "FAQ",
    "explore.overline": "Forbeholdt kunnskap",
    "explore.title": "Klubben, i detalj.",
    "explore.intro": "Tre interne rom forbeholdt medlemmene: vår driftsfilosofi, den juridiske og tilsynsmessige strukturen og de vanligste spørsmålene før du investerer.",
    "explore.card_philosophy_desc": "En skandinavisk modell, anvendt på en middelhavsklient. Hvordan Borealis fungerer i fire trinn.",
    "explore.card_compliance_desc": "Juridisk struktur, tilsynsmyndighet, innskuddsgarantier, interessekonflikter, GDPR.",
    "explore.card_faq_desc": "Seks spørsmål før du investerer. Direkte svar, ingen liten asterisk.",
    "explore.read": "Les mer →",
    "explore.back": "← Tilbake til Utforsk",

    "nav.kyc": "KYC",
    "kyc.overline": "KYC-onboarding",
    "kyc.title": "Dokumentene dine, i orden.",
    "kyc.intro": "For å aktivere Borealis Reserve-kontoen din fullstendig trenger vi at du signerer noen dokumenter og laster opp andre. En engangsoperasjon, i ro.",
    "kyc.progress_label": "Fremdrift",
    "kyc.progress_value": "{approved} av {total} godkjent",
    "kyc.complete_banner": "Onboarding fullført. Kontoen din er fullt operativ.",
    "kyc.status_pending": "Gjenstår",
    "kyc.status_submitted": "Under gjennomgang",
    "kyc.status_approved": "Godkjent",
    "kyc.status_rejected": "Må gjennomgås",
    "kyc.kind_sign": "Digital signatur",
    "kyc.kind_upload": "Dokumentopplasting",
    "kyc.action_view_template": "Åpne mal",
    "kyc.action_download_template": "Last ned mal",
    "kyc.action_sign": "Signer digitalt",
    "kyc.action_resign": "Signer igjen",
    "kyc.action_upload": "Last opp fil",
    "kyc.action_replace": "Erstatt",
    "kyc.action_download_signed": "Last ned signert kopi",
    "kyc.action_download_uploaded": "Se opplastet dokument",
    "kyc.upload_accept": "PDF, JPG, PNG · maks 12 MB",
    "kyc.uploading": "Laster opp…",
    "kyc.signing": "Signerer…",
    "kyc.last_comment": "Compliance-merknad",
    "kyc.sign_modal_title": "Enkel elektronisk signatur",
    "kyc.sign_modal_intro": "Du er i ferd med å signere elektronisk dokumentet <strong>{title}</strong>. Signaturen vil bli registrert med revisjonsspor (UTC-tidsstempel, IP, SHA-256-hash) i henhold til EU-forordning 910/2014 (eIDAS).",
    "kyc.sign_modal_checkbox": "Jeg erklærer at jeg har lest, forstått og fullt ut akseptert innholdet i dokumentet.",
    "kyc.sign_modal_confirm": "Signer nå",
    "kyc.sign_modal_cancel": "Avbryt",
    "kyc.sign_modal_disclaimer": "Enkel elektronisk signatur produserer de juridiske virkningene av en skriftlig tegning mellom partene.",

    "calc.overline": "Avkastningssimulator",
    "calc.title": "Estimer din horisont.",
    "calc.intro": "Beveg glidebryteren for å simulere forventet avkastning over de første 12 månedene av kapitalbindingen. Verdiene gjenspeiler de gjeldende pakkene som Borealis Reserve-teamet publiserer.",
    "calc.amount_label": "Kapital å bidra med",
    "calc.duration_label": "Bindingstid",
    "calc.duration_12": "12 måneder",
    "calc.duration_24": "24 måneder",
    "calc.duration_36": "36 måneder",
    "calc.duration_open": "Åpen",
    "calc.tier_label": "Gjeldende pakke",
    "calc.rate_gross": "Årlig brutto avkastning",
    "calc.rate_net": "Estimert årlig netto avkastning",
    "calc.mngmt_fee": "Forvaltningshonorar",
    "calc.perf_fee": "Avkastningshonorar",
    "calc.lock_period": "Bindingstid",
    "calc.gross_profit": "Bruttofortjeneste ({months}m)",
    "calc.net_profit": "Nettofortjeneste ({months}m)",
    "calc.maturity_value": "Forfallsverdi",
    "calc.chart_title": "Kapitalvekst",
    "calc.chart_legend": "{months}-måneders projeksjon",
    "calc.chart_open_note": "Indikativ 12-måneders projeksjon",
    "calc.no_plan": "Ingen pakke er for øyeblikket aktiv for denne kapitalen. Kontakt din Relationship Manager.",
    "calc.disclaimer": "Ikke-bindende simulering for illustrasjonsformål. Faktiske vilkår er de i pakken tegnet på bidragstidspunktet.",
    "disc.title": "Regulatorisk informasjon",
    "disc.past": "Historisk avkastning er ikke en pålitelig indikator for fremtidige resultater.",
    "disc.risk": "Investert kapital er utsatt for risiko. Verdien av investeringer kan falle så vel som stige.",
    "disc.mkt": "Markedsføringskommunikasjon. Dette utgjør ikke finansiell rådgivning eller et offentlig tilbud.",
    "nav.plans": "Planer",
    "plans.overline": "Tegning",
    "plans.title": "Investeringsplaner",
    "plans.intro": "Utforsk de medlemsbegrensede pakkene med fast avkastning. Velg en plan for å sende en tegningsforespørsel til Finance & Compliance-teamet.",
    "plans.loading": "Laster planer…",
    "plans.kyc_title": "Utforsk fritt",
    "plans.kyc_body": "Bla gjennom pakkene, last ned KID-ene og simuler avkastning. Du kan sende tegningsforespørselen med en gang: for å aktivere den ber vi deg fullføre KYC-verifiseringen.",
    "plans.kyc_cta": "Gå til KYC",
    "plans.partner_title": "Partnermodus",
    "plans.partner_body": "Presentasjonsvisning: bruk produktarkene, avkastningssimulatoren og KID-ene til å introdusere Borealis Reserve for dine kontakter. Tegning av planer er forbeholdt faktiske kunder.",
    "plans.partner_showcase_badge": "Kun presentasjon",
    "plans.partner_your_code": "Din kode",
    "plans.partner_copy_link": "Kopier lenken din →",
    "plans.range": "Bidragsintervall",
    "plans.checkbox": "Jeg har lest informasjonen og ønsker å be om denne planen",
    "plans.request_cta": "Be om tegning",
    "plans.pending_badge": "Forespørsel venter",
    "plans.amount_label": "Veiledende beløp (valgfritt)",
    "plans.amount_help": "Innenfor planens intervall. Endelig beløp avtales med teamet.",
    "plans.note_label": "Merknad til teamet (valgfritt)",
    "plans.modal_title": "Bekreft tegningsforespørsel",
    "plans.modal_body": "Ved å klikke «Bekreft og send» vil forespørselen din for planen «{plan}» bli sendt til Finance & Compliance-teamet. Du vil motta flere detaljer via e-post og telefon, inkludert — ved aksept — koordinatene for bankoverføringen. Ingen betaling forfaller på dette stadiet.",
    "plans.modal_confirm": "Bekreft og send",
    "plans.modal_cancel": "Avbryt",
    "plans.toast_ok": "Forespørsel sendt. Finance & Compliance-teamet vil kontakte deg snart.",
    "plans.toast_err": "Kunne ikke sende forespørselen. Prøv igjen.",
    "plans.my_requests": "Mine forespørsler",
    "plans.status_pending": "Venter",
    "plans.status_awaiting_kyc": "Venter på KYC",
    "plans.toast_ok_kyc": "Forespørselen er sendt. Fullfør KYC-verifiseringen nå: så snart den er godkjent, går forespørselen videre til investeringsteamet.",
    "plans.status_approved": "Godkjent",
    "plans.status_rejected": "Ikke akseptert",
    "plans.no_requests": "Du har ikke sendt inn noen forespørsler ennå.",
    "plans.kid_download": "Last ned KID (PDF)",
    "plans.kid_ack_label": "Jeg har lest og forstått nøkkelinformasjonsdokumentet (KID) for dette produktet",
    "plans.kid_modal_hint": "Før du sender forespørselen, vennligst les produktets nøkkelinformasjonsdokument (KID).",
    "plans.allocation_title": "Aktivaallokering",
    "plans.volatility_target": "Volatilitetsmål",
    "plans.allocation_show": "Vis aktivaallokering",
    "plans.allocation_hide": "Skjul aktivaallokering",
    "plans.allocation_note": "Veiledende målallokering til illustrasjon, rebalansert jevnlig av Borealis' investeringskomité. Faktiske vekter kan variere med markedsforholdene.",
    "alloc.global_equity_etf": "Globale aksje-ETF-er",
    "alloc.global_equity": "Globale aksjer",
    "alloc.investment_grade_bonds": "Investment Grade-obligasjoner",
    "alloc.money_market_short": "Pengemarked & kort durasjon",
    "alloc.money_market": "Pengemarked",
    "alloc.alternative_ucits_funds": "Alternative UCITS-fond",
    "alloc.alternative_ucits_strategies": "Alternative UCITS-strategier",
    "alloc.alternative_strategies": "Alternative strategier",
    "alloc.gold_commodities": "Gull & råvarer",
    "alloc.commodities": "Råvarer",
    "alloc.private_markets": "Private markeder",
    "alloc.private_equity": "Private Equity",
    "alloc.private_credit": "Private Credit",
    "alloc.real_assets": "Realaktiva",
    "alloc.cash": "Kontanter",
    "alloc.tactical_cash": "Taktisk likviditet",
    "calc.months": "måneder",
    "calc.lock_open": "Åpen",

    "team.overline": "Forvaltningsteam",
    "team.title": "Menneskene bak kapitalen din.",
    "team.intro": "Profesjonelle som strukturerer din strategi. Regulerte profiler, kan verifiseres på FINRA.",
    "team.crd": "CRD#",
    "team.years_experience": "års erfaring",
    "team.finra_link": "FINRA-profil →",
    "team.locked_title": "Reservert seksjon",
    "team.locked_body": "Forvaltningsteamet blir synlig etter at KYC-prosessen er fullført. Fortsett til KYC-seksjonen for å fullføre dokumentene dine.",

    "nav.events": "Arrangementer",
    "events.overline": "Reservert kalender",
    "events.title": "Klubbens avtaler.",
    "events.intro": "Private middager, makrobriefinger, rundbordskonferanser. Alle sammenkomster krever forespørsel om deltakelse, godkjent av Compliance-teamet.",
    "events.locked_title": "Reservert seksjon",
    "events.locked_body": "Arrangementskalenderen blir synlig etter at KYC-prosessen er fullført. Fortsett til KYC-seksjonen for å låse den opp.",
    "events.section_upcoming": "Kommende arrangementer",
    "events.section_past": "Tidligere arrangementer",
    "events.empty_upcoming": "Ingen arrangementer i kalenderen for øyeblikket. Vi sender deg en invitasjon så snart en blir publisert.",
    "events.empty_past": "Ingen tidligere arrangementer å vise ennå.",
    "events.starts_at": "Når",
    "events.venue": "Hvor",
    "events.dress_code": "Antrekk",
    "events.capacity": "Plasser",
    "events.rsvp_request": "Be om deltakelse",
    "events.rsvp_cancel": "Trekk forespørselen",
    "events.rsvp_status_pending": "Forespørsel sendt",
    "events.rsvp_status_accepted": "Deltakelse bekreftet",
    "events.rsvp_status_declined": "Forespørsel ikke akseptert",
    "events.rsvp_admin_comment": "Compliance-merknad",
    "events.rsvp_modal_title": "Deltakerforespørsel",
    "events.rsvp_modal_intro": "Forespørselen vil bli vurdert av Compliance-teamet, basert på profilen din og fartstid i klubben. Du vil motta bekreftelse via e-post innen 2 virkedager.",
    "events.rsvp_modal_plusone": "Jeg kommer med en gjest",
    "events.rsvp_modal_notes": "Merknader (kostbehov, spesielle krav)…",
    "events.rsvp_modal_submit": "Send forespørsel",
    "events.rsvp_modal_cancel": "Avbryt",

    "events.criteria_overline": "Deltakerkriterier",
    "events.criteria_title": "Klubbfartstid og arrangementstilgang.",
    "events.criteria_intro": "Deltakerforespørsler vurderes manuelt av Compliance-teamet basert på fartstid i klubben. Referansekriteriene er listet under.",
    "events.criteria_member": "Medlem · tilgang til webinarer og nettkonferanser",
    "events.criteria_senior": "Senior Member (3 år) · også tilgang til rundbordskonferanser",
    "events.criteria_legacy": "Legacy Member (5 år) · også tilgang til private middager",
    "events.criteria_patron": "Patron Member (7 år) · også tilgang til eksklusive opplevelser",
    "events.criteria_founders": "Founders Circle (10 år) · absolutt prioritet og invitasjoner til arrangementer med begrenset plass",
    "events.criteria_your_tenure": "Din fartstid i klubben er {years} år · gjeldende nivå {tier}.",

    "membership.tier.member": "Medlem",
    "membership.tier.senior_member": "Senior Member",
    "membership.tier.legacy_member": "Legacy Member",
    "membership.tier.patron_member": "Patron Member",
    "membership.tier.founders_circle": "Founders Circle",

    "calc.tier_foundation": "Borealis Foundation",
    "calc.tier_prime": "Borealis Prime",
    "calc.tier_signature": "Borealis Signature",
    "calc.tier_sovereign": "Borealis Sovereign",
    "calc.tier_legacy": "Borealis Legacy",

    // ---------- Introducer portal ----------
    "intro.nav_dashboard": "Dashbord",
    "intro.nav_referrals": "Henvisninger",
    "intro.nav_contract": "Kontrakt",
    "intro.nav_guide": "Håndbok",
    "intro.contract_overline": "Formidlingskontrakt",
    "intro.contract_heading_a": "Din kontrakt",
    "intro.contract_heading_em": "med Borealis Reserve.",
    "intro.contract_intro": "Henvisnings- og formidlingsavtale. Signer digitalt for å begynne å operere som autorisert Borealis Reserve Introducer.",
    "intro.contract_status_signed": "Signert",
    "intro.contract_status_pending": "Skal signeres",
    "intro.contract_signed_on": "Signert",
    "intro.contract_download": "Last ned PDF",
    "intro.contract_sign_overline": "Digital signatur",
    "intro.contract_sign_heading": "Signer kontrakten",
    "intro.contract_sign_body": "Vennligst les kontrakten ovenfor. Ved å klikke \"Signer digitalt\" bekrefter du å ha lest og forstått alle klausulene. Signaturen er juridisk bindende iht. italiensk art. 20 CAD (D.Lgs. 82/2005) og gir samme effekt som skriftlig signatur (art. 2702 c.c.).",
    "intro.contract_ack_label": "Jeg har lest, forstått og godtar fullt ut alle artikler i kontrakten. Jeg er kontoinnehaver og fortsetter med digital signatur.",
    "intro.contract_sign_cta": "Signer digitalt",
    "intro.contract_signing": "Signerer…",
    "intro.contract_legal_note": "IP, tidsstempel og hash logges",
    "intro.contract_signed_overline": "Signert",
    "intro.contract_signed_heading": "Kontrakt aktiv.",
    "intro.contract_signed_body": "En signert kopi med tidsstempel, kildens IP og SHA-256 integritetshash er sendt til deg på e-post. Du kan alltid gjennomgå og laste den ned herfra.",
    "intro.contract_back_dashboard": "Tilbake til dashbord",
    "intro.contract_back": "Introducer-dashbord",
    "intro.contract_banner_overline_pending": "Handling påkrevd",
    "intro.contract_banner_title_pending": "Signer henvisningskontrakten",
    "intro.contract_banner_body_pending": "Før du starter henvisningsaktivitet må du signere kontrakten digitalt. Tar 60 sekunder.",
    "intro.contract_banner_cta_pending": "Gå til kontrakt",
    "intro.contract_banner_overline_signed": "Kontrakt aktiv",
    "intro.contract_banner_body_signed": "Signert {date}. Signert PDF sendt på e-post og alltid tilgjengelig i kontraktsområdet.",
    "intro.contract_banner_view_signed": "Gjennomgå kontrakt",
    "intro.dashboard_overline": "Partnerportal",
    "intro.dashboard_heading_a": "Velkommen,",
    "intro.dashboard_intro": "Ditt private område — se hvor mange kontakter du har introdusert til Borealis Reserve, samlet AUM og opparbeidet provisjon. Personvernet til de henviste kundene er beskyttet: navn og detaljer vises aldri.",
    "intro.kpi_referrals_total": "Totale henvisninger",
    "intro.kpi_referrals_active_sub": "{count} aktive",
    "intro.kpi_aum": "Introdusert AUM",
    "intro.kpi_deposits_count": "{count} aktive innskudd",
    "intro.kpi_accrued": "Opparbeidet (netto)",
    "intro.kpi_accrued_sub": "Brutto: {gross}",
    "intro.kpi_yearly_estimate": "Årlig estimat (netto)",
    "intro.kpi_commission_rate": "{pct}% p.a. på AUM",
    "intro.share_overline": "Din lenke",
    "intro.share_heading": "Del Borealis Reserve.",
    "intro.share_body": "Hver kontakt som kommer via lenken nedenfor knyttes automatisk til din promokode.",
    "intro.copy_link": "Kopier lenke",
    "intro.copied": "Kopiert",
    "intro.code_label": "Kode",
    "intro.privacy_overline": "Personvern henvisninger",
    "intro.privacy_body": "For å respektere konfidensialiteten til kundene du introduserer, ser du aldri navn, e-post, telefonnumre eller KYC-dokumenter. Du ser bare anonymiserte aggregater (antall, AUM, din opparbeidede provisjon). Borealis håndterer alle forretningsforhold direkte.",
    "intro.dashboard_view_statements": "Månedlige oppgjør",
    "intro.dashboard_view_statements_sub": "Last ned provisjonsrapportene dine",
    "intro.stmt_overline": "Provisjoner",
    "intro.stmt_heading_a": "Månedlige",
    "intro.stmt_heading_em": "oppgjør.",
    "intro.stmt_intro": "På slutten av hver måned publiserer Borealis-teamet provisjonsrapporten din her: bruttobeløp, eventuell kildeskatt og netto utbetalt. Hver PDF er kontrasignert og arkivert for skatteformål.",
    "intro.stmt_col_period": "Periode",
    "intro.stmt_col_ref": "Oppgjør nr.",
    "intro.stmt_col_gross": "Brutto",
    "intro.stmt_col_withholding": "Kildeskatt",
    "intro.stmt_col_net": "Netto",
    "intro.stmt_col_download": "Last ned",
    "intro.stmt_loading": "Laster…",
    "intro.stmt_empty": "Ingen oppgjør publisert ennå. Det første er tilgjengelig fra måneden etter at kontrakten er signert.",
    "intro.stmt_footnote": "Skattedata behandles i henhold til gjeldende regelverk. For spørsmål skriv til finance@borealisreserve.com.",
    "intro.dashboard_playbook": "Partner-håndbok",
    "intro.dashboard_playbook_sub": "Trinnvis guide til partnerportalen",
    "intro.dashboard_playbook_cta": "Åpne håndboken",
    "intro.referrals_overline": "Dine henvisninger",
    "intro.referrals_heading_a": "Kontakter",
    "intro.referrals_heading_em": "anonymisert.",
    "intro.referrals_intro": "Kundene du har introdusert — kun samlede indikatorer. Ingen personopplysninger eksponeres noensinne.",
    "intro.referrals_loading": "Laster…",
    "intro.referrals_empty": "Ingen henvisninger enda — del din lenke eller promokode.",
    "intro.col_ref": "Henvisning",
    "intro.col_activated": "Aktivert",
    "intro.col_status": "Status",
    "intro.col_principal": "AUM-bidrag",
    "intro.col_deposits": "Innskudd",
    "intro.status_active": "Aktiv",
    "intro.status_pending": "Venter",
    "intro.status_suspended": "Suspendert",
    "intro.referrals_privacy_footnote": "* Kolonnene er avrundet til aktiveringsmåneden for å beskytte kundens identitet. Borealis kan levere ytterligere sertifiserte rapporter etter skriftlig forespørsel.",

    // ---------- Public referral landing ----------
    "referral.brand_tagline": "Privat klientportal",
    "referral.checking": "Verifiserer kode…",
    "referral.invalid_overline": "Ugyldig lenke",
    "referral.invalid_title": "Kode ikke gjenkjent",
    "referral.invalid_body": "Promokoden er ikke lenger gyldig eller er deaktivert. Kontakt den som delte lenken med deg for å motta en oppdatert kode.",
    "referral.back_to_login": "Gå til innlogging",
    "referral.overline": "Du har blitt introdusert",
    "referral.heading_a": "Introdusert av",
    "referral.heading_b": ", er du ett skritt unna Borealis Reserve.",
    "referral.intro": "Legg igjen dine kontaktdetaljer: en Borealis-rådgiver tar kontakt for en diskret første samtale. Ingen forpliktelser.",
    "referral.field_name": "Fullt navn",
    "referral.field_email": "E-post",
    "referral.field_phone": "Telefon",
    "referral.field_message": "Melding (valgfritt)",
    "referral.submitting": "Sender…",
    "referral.cta": "Send forespørsel",
    "referral.privacy_note": "Dine data behandles utelukkende for å vurdere din interesse for Borealis Reserve, i samsvar med EU-forordning 2016/679 (GDPR).",
    "referral.thanks_overline": "Takk",
    "referral.thanks_title": "Forespørsel mottatt.",
    "referral.thanks_body": "En Borealis-rådgiver vil kontakte deg snart. For hastesaker, skriv til info@borealisreserve.com.",
    "referral.err_required": "Navn og e-post er obligatorisk.",
    "referral.err_generic": "Noe gikk galt. Vennligst prøv igjen.",

  },
  de: {
    "switcher.label": "Sprache",

    "login.brand_tagline": "Private-Client-Portal",
    "login.overline": "Geschützter Zugang",
    "login.heading_a": "Ihre",
    "login.heading_em": "Ruhe",
    "login.heading_b": "erwartet Sie.",
    "login.intro": "Nur für eingeladene Mitglieder. Keine öffentliche Registrierung.",
    "login.email": "E-Mail",
    "login.password": "Passwort",
    "login.cta": "Zum Portal",
    "login.cta_loading": "Anmeldung läuft…",
    "login.forgot": "Passwort vergessen?",
    "login.no_invite_q": "Noch keine Einladung erhalten?",
    "login.no_invite_a": "Schreiben Sie an",
    "login.footer_brand": "Borealis Reserve · Privater Club",
    "login.checking_session": "Sitzung wird geprüft…",

    "login.twofa_overline": "Zwei-Faktor-Authentifizierung",
    "login.twofa_heading_a": "Geben Sie den",
    "login.twofa_heading_em": "Code",
    "login.twofa_heading_b": "aus Ihrer Authenticator-App ein.",
    "login.twofa_intro": "Öffnen Sie die Authenticator-App (Google Authenticator, Authy, 1Password) und geben Sie den 6-stelligen Code ein.",
    "login.twofa_code_label": "6-stelliger Code",
    "login.twofa_cta": "Verifizieren und anmelden",
    "login.twofa_back": "Zurück zur Anmeldung",

    "invite.brand_tagline": "Einladungs-Aktivierung",
    "invite.overline": "Willkommen",
    "invite.heading_em": "legen Sie",
    "invite.heading_lead": "Ihr Passwort fest.",
    "invite.intro": "Sie aktivieren das Konto für",
    "invite.field_name": "Vollständiger Name",
    "invite.field_password": "Neues Passwort",
    "invite.field_confirm": "Passwort bestätigen",
    "invite.cta": "Konto aktivieren und einloggen",
    "invite.cta_loading": "Aktivierung läuft…",
    "invite.checking": "Einladung wird geprüft…",
    "invite.error_title": "Ungültige Einladung",
    "invite.error_explainer": "Einladungen sind personalisiert, einmalig nutzbar und 7 Tage gültig.",
    "invite.error_assistance": "Für Unterstützung schreiben Sie an",
    "invite.err_password_min": "Das Passwort muss mindestens 8 Zeichen lang sein.",
    "invite.err_password_mismatch": "Die Passwörter stimmen nicht überein.",
    "invite.greeting_named": "{name},",
    "invite.greeting_default": "Mitglied,",

    "forgot.overline": "Absolute Vertraulichkeit",
    "forgot.title_a": "Passwort-",
    "forgot.title_em": "Wiederherstellung",
    "forgot.heading_a": "Wir setzen Passwörter nicht",
    "forgot.heading_em": "online",
    "forgot.heading_b": " zurück.",
    "forgot.p1": "Zum Schutz Ihres Kapitals wird das Passwort ausschließlich über einen verifizierten Kanal mit einem Mitglied unseres Reserve-Teams zurückgesetzt.",
    "forgot.how_overline": "So gehen Sie vor",
    "forgot.step1": "Schreiben Sie von einer Ihrem Relationship Manager bereits bekannten E-Mail-Adresse",
    "forgot.step2": "Sie erhalten innerhalb von 24 Stunden einen Verifizierungsanruf",
    "forgot.step3": "Nach erfolgter Verifizierung stellen wir eine neue einmalige Einladung aus",
    "forgot.cta_mail": "Schreiben Sie an clients@",
    "forgot.cta_compliance": "Compliance",
    "forgot.reminder_title": "Hinweis",
    "forgot.reminder_body": "wir fragen niemals per E-Mail oder Telefon nach Passwörtern, OTPs oder Dokumenten, bevor Ihr Relationship Manager Sie persönlich verifiziert hat.",
    "forgot.close": "Schließen",
    "forgot.form_email_label": "Konto-E-Mail-Adresse",
    "forgot.form_submit": "Anfrage senden",
    "forgot.form_submitting": "Wird gesendet…",
    "forgot.error_generic": "Etwas ist schiefgelaufen. Bitte versuchen Sie es später erneut.",
    "forgot.confirmation_overline": "Anfrage eingegangen",
    "forgot.confirmation_p1": "Wir haben Ihre Anfrage erhalten. Ein Mitglied des Compliance-Teams wird Sie innerhalb von 24 Stunden telefonisch zur Identitätsprüfung kontaktieren.",
    "forgot.confirmation_p2": "Wenn die E-Mail-Adresse einem aktiven Konto entspricht, erhalten Sie eine Bestätigung. Andernfalls wird aus Sicherheitsgründen keine E-Mail versendet.",

    "reset.overline": "Passwort-Wiederherstellung",
    "reset.title_a": "Neues",
    "reset.title_em": "Passwort festlegen",
    "reset.loading": "Link wird geprüft…",
    "reset.invalid_title": "Ungültiger Link",
    "reset.invalid_expired": "Dieser Link ist abgelaufen. Wenden Sie sich an Ihren Relationship Manager, um einen neuen zu erhalten.",
    "reset.invalid_used": "Dieser Link wurde bereits verwendet. Falls Sie das nicht waren, kontaktieren Sie umgehend legal@borealisreserve.com.",
    "reset.invalid_generic": "Der Link ist nicht gültig. Überprüfen Sie, ob Sie die vollständige URL aus der E-Mail kopiert haben, oder fordern Sie einen neuen Link an.",
    "reset.back_to_login": "Zurück zur Anmeldung",
    "reset.heading_a": "Wählen Sie ein",
    "reset.heading_em": "neues Passwort",
    "reset.heading_b": ".",
    "reset.intro_for_email": "Sie legen ein neues Passwort fest für {email}.",
    "reset.new_password": "Neues Passwort",
    "reset.confirm_password": "Neues Passwort bestätigen",
    "reset.policy_hint": "Mindestens 10 Zeichen, mit mindestens einem Kleinbuchstaben, einem Großbuchstaben, einer Ziffer und einem Symbol.",
    "reset.mismatch": "Die beiden Passwörter stimmen nicht überein.",
    "reset.submit": "Neues Passwort festlegen",
    "reset.submitting": "Wird aktualisiert…",
    "reset.error_generic": "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder kontaktieren Sie Ihren Relationship Manager.",
    "reset.done_title": "Passwort aktualisiert ✓",
    "reset.done_p1": "Ihr neues Passwort ist aktiv. Aus Sicherheitsgründen wurden alle vorherigen Sitzungen beendet: Bitte melden Sie sich erneut an.",
    "reset.go_login": "Zur Anmeldung",

    "change.overline": "Kontosicherheit",
    "change.title_a": "Passwort",
    "change.title_em": "ändern",
    "change.heading_mandatory": "Legen Sie ein persönliches Passwort fest.",
    "change.heading_regular": "Passwort ändern.",
    "change.intro_mandatory": "Sie haben sich mit einem von unserem Team bereitgestellten temporären Passwort angemeldet. Wählen Sie ein persönliches Passwort, um fortzufahren.",
    "change.intro_regular": "Aktualisieren Sie das Passwort Ihres Kontos. Nach der Änderung werden alle aktiven Sitzungen beendet.",
    "change.current_label": "Aktuelles Passwort (oder temporäres)",
    "change.new_label": "Neues Passwort",
    "change.confirm_label": "Neues Passwort bestätigen",
    "change.policy_hint": "Mindestens 10 Zeichen, mit mindestens einem Kleinbuchstaben, einem Großbuchstaben, einer Ziffer und einem Symbol.",
    "change.mismatch": "Die beiden Passwörter stimmen nicht überein.",
    "change.same_as_current": "Das neue Passwort muss sich vom aktuellen unterscheiden.",
    "change.submit": "Passwort aktualisieren",
    "change.submitting": "Wird aktualisiert…",
    "change.error_generic": "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",

    "nav.dashboard": "Dashboard",
    "nav.positions": "Festgeld",
    "nav.statements": "Kontoauszüge",
    "nav.logout": "Abmelden",
    "pwa.install": "App installieren",
    "pwa.ios_title": "Auf iPhone / iPad installieren",
    "pwa.ios_step1": "Tippen Sie auf die Teilen-Schaltfläche in der Safari-Leiste (Quadrat mit Pfeil nach oben).",
    "pwa.ios_step2": "Scrollen Sie in der Liste und wählen Sie „Zum Home-Bildschirm“, dann mit „Hinzufügen“ bestätigen.",
    "pwa.ios_note": "Die Borealis-Reserve-App erscheint auf Ihrem Home-Bildschirm und öffnet sich im Vollbild — wie eine native App.",
    "header.private_portal": "Privates Portal",

    "footer.members_only": "© {year} Borealis Reserve · Nur für Mitglieder",
    "footer.licenses_title": "Lizenzen und Geschäftsadressen",
    "footer.broker_title": "Von der FINRA regulierte Brokerage Firm",
    "footer.broker_subline": "ABG Securities, Inc. · Finanshuset (USA) Securities, Inc. · FIBA Nordic Securities, Inc. · ABG Sundal Collier Inc.",
    "footer.main_address": "Hauptadresse",
    "footer.investment_firm": "Wertpapierhaus",
    "footer.registered_office": "Registrierter Sitz",
    "footer.city": "Stadt",
    "footer.country": "Land",
    "footer.enrolment_no": "Registernummer",
    "footer.finanstilsynet_id": "Finanstilsynet-ID",
    "footer.finra_link": "FINRA-Profil →",
    "footer.consob_link": "Consob →",
    "footer.finanstilsynet_link": "Finanstilsynet →",
    "footer.legal_note": "Borealis Reserve ist ein Service, der ausschließlich Mitgliedern des Clubs vorbehalten ist. Die in den Portalmaterialien dargestellten Teilnahmebedingungen und Renditen stellen weder ein öffentliches Angebot noch eine Anlageaufforderung im Sinne der geltenden Vorschriften dar.",

    "dash.overline": "Willkommen",
    "dash.greeting": "Guten Tag, {name}.",
    "dash.greeting_default": "Mitglied",
    "dash.intro": "Ihr Kapital arbeitet jede Sekunde. Hier sehen Sie die Echtzeit-Übersicht Ihres Kontos.",
    "dash.loading": "Wird geladen…",
    "dash.capital_managed": "Verwaltetes Kapital",
    "dash.on_n_deposits_one": "Auf {n} aktivem Festgeld.",
    "dash.on_n_deposits": "Auf {n} aktiven Festgeldern.",
    "dash.updated_at": "Aktualisiert am {date}.",
    "dash.today_accrued": "Heute aufgelaufen",
    "dash.linear_accrual": "Lineare tägliche Verzinsung",
    "dash.per_day": "{amount}/Tag",
    "dash.kpi_accrued": "Bisher aufgelaufen",
    "dash.kpi_monthly": "Monatliche Schätzung",
    "dash.kpi_yearly": "Jährliche Schätzung",
    "dash.kpi_statements": "Verfügbare Kontoauszüge",
    "dash.kyc_overline": "Onboarding",
    "dash.kyc_title": "Vervollständigen Sie Ihre Unterlagen",
    "dash.kyc_progress": "{approved} von {total} Dokumenten genehmigt",
    "dash.kyc_submitted": "{n} in Prüfung",
    "dash.kyc_rejected": "{n} erneut hochzuladen",
    "dash.kyc_note": "Voller Zugriff auf Pläne, Veranstaltungen und das Team wird nach Abschluss des KYC freigeschaltet.",
    "dash.kyc_cta": "Jetzt vervollständigen",
    "dash.next_coupon": "Nächster Kupon",
    "dash.principal": "Kapital",
    "dash.coupon_amount": "Kuponbetrag",
    "dash.your_deposits": "Ihre Festgelder",
    "dash.see_all": "Alle ansehen →",
    "dash.net_rate": "Nettozins",
    "dash.accrued": "Aufgelaufen",
    "dash.daily": "Pro Tag",
    "dash.auto_reinvest_active": "Auto-Reinvestition aktiv",
    "dash.deposit": "Festgeld",
    "dash.days_progress": "{elapsed}/{total} T",

    "pos.overline": "Positionen",
    "pos.heading_a": "Ihre",
    "pos.heading_em": "Festgelder",
    "pos.heading_b": ".",
    "pos.intro": "Lineare tägliche Verzinsung, Kapitalisierung nur bei aktiver &quot;Auto-Reinvestition&quot; bei Fälligkeit.",
    "pos.col_deposit": "Festgeld",
    "pos.col_principal": "Kapital",
    "pos.col_gross_rate": "Bruttozins",
    "pos.col_fee": "Gebühr",
    "pos.col_net_rate": "Nettozins",
    "pos.col_start": "Beginn",
    "pos.col_end": "Fälligkeit",
    "pos.col_accrued": "Netto aufgelaufen",
    "pos.col_daily": "/Tag",
    "pos.col_maturity_value": "Wert bei Fälligkeit",
    "pos.auto_reinvest_short": "Auto-Reinvest",
    "pos.empty": "Noch keine aktiven Festgelder.",

    "stmt.overline": "Dokumente",
    "stmt.heading_a": "Kontoauszüge und",
    "stmt.heading_em": "Bescheinigungen",
    "stmt.heading_b": ".",
    "stmt.intro": "Vom Back-Office veröffentlicht. Laden Sie die signierten PDFs für Ihre Buchhaltung oder Ihren Steuerberater herunter.",
    "stmt.col_period": "Zeitraum",
    "stmt.col_kind": "Typ",
    "stmt.col_label": "Bezeichnung",
    "stmt.col_published": "Veröffentlicht",
    "stmt.col_actions": "Aktionen",
    "stmt.kind_quarterly": "Quartalsweise",
    "stmt.kind_monthly": "Monatlich",
    "stmt.kind_annual": "Jährlich",
    "stmt.kind_coupon": "Kupon",
    "stmt.label_default": "Kontoauszug",
    "stmt.download": "Herunterladen",
    "stmt.empty": "Noch keine Kontoauszüge verfügbar.",

    "bank.overline": "Bankverbindung",
    "bank.title": "Wohin Sie Ihr Kapital überweisen",
    "bank.intro": "Überweisungen ausschließlich auf die unten angegebenen Bankdaten, die für Ihr Konto reserviert sind.",
    "bank.empty_title": "Wird zugewiesen",
    "bank.empty_body": "Ihr Relationship Manager finalisiert gerade die Ihrem Konto zugewiesene Bankverbindung. Sie werden benachrichtigt, sobald diese verfügbar ist.",
    "bank.beneficiary": "Begünstigter",
    "bank.iban": "IBAN",
    "bank.bic": "BIC / SWIFT",
    "bank.bank": "Bank",
    "bank.reference": "Vorgeschlagener Verwendungszweck",
    "bank.copy": "Kopieren",
    "bank.copied": "Kopiert",
    "bank.disclaimer": "Überprüfen Sie diese Bankdaten immer mit Ihrem Ansprechpartner, bevor Sie eine Überweisung veranlassen. Borealis Reserve fordert niemals IBAN-Änderungen per E-Mail an.",

    "nav.explore": "Entdecken",
    "explore.philosophy": "Philosophie",
    "explore.compliance": "Compliance",
    "explore.faq": "FAQ",
    "explore.overline": "Reserviertes Wissen",
    "explore.title": "Der Club im Detail.",
    "explore.intro": "Drei interne Bereiche für Mitglieder: unsere Betriebsphilosophie, die rechtliche und aufsichtliche Struktur und die häufigsten Fragen vor einer Anlage.",
    "explore.card_philosophy_desc": "Ein skandinavisches Modell, angewandt auf einen mediterranen Kunden. Wie Borealis in vier Schritten funktioniert.",
    "explore.card_compliance_desc": "Rechtliche Struktur, Aufsichtsbehörde, Einlagensicherung, Interessenkonflikte, DSGVO.",
    "explore.card_faq_desc": "Sechs Fragen vor einer Anlage. Direkte Antworten, ohne Sternchen.",
    "explore.read": "Mehr erfahren →",
    "explore.back": "← Zurück zu Entdecken",

    "nav.kyc": "KYC",
    "kyc.overline": "KYC-Onboarding",
    "kyc.title": "Ihre Dokumente, in Ordnung.",
    "kyc.intro": "Um Ihr Borealis-Reserve-Konto vollständig zu aktivieren, müssen Sie einige Dokumente unterzeichnen und andere hochladen. Eine einmalige Operation, in Ruhe.",
    "kyc.progress_label": "Fortschritt",
    "kyc.progress_value": "{approved} von {total} genehmigt",
    "kyc.complete_banner": "Onboarding abgeschlossen. Ihr Konto ist vollständig einsatzbereit.",
    "kyc.status_pending": "Zu erledigen",
    "kyc.status_submitted": "In Prüfung",
    "kyc.status_approved": "Genehmigt",
    "kyc.status_rejected": "Zu überarbeiten",
    "kyc.kind_sign": "Digitale Signatur",
    "kyc.kind_upload": "Dokument-Upload",
    "kyc.action_view_template": "Vorlage öffnen",
    "kyc.action_download_template": "Vorlage herunterladen",
    "kyc.action_sign": "Digital unterschreiben",
    "kyc.action_resign": "Erneut unterschreiben",
    "kyc.action_upload": "Datei hochladen",
    "kyc.action_replace": "Ersetzen",
    "kyc.action_download_signed": "Signierte Kopie herunterladen",
    "kyc.action_download_uploaded": "Hochgeladenes Dokument ansehen",
    "kyc.upload_accept": "PDF, JPG, PNG · max. 12 MB",
    "kyc.uploading": "Wird hochgeladen…",
    "kyc.signing": "Wird signiert…",
    "kyc.last_comment": "Compliance-Hinweis",
    "kyc.sign_modal_title": "Einfache elektronische Signatur",
    "kyc.sign_modal_intro": "Sie sind dabei, Ihre elektronische Signatur auf das Dokument <strong>{title}</strong> anzubringen. Die Signatur wird mit Audit-Trail (UTC-Zeitstempel, IP, SHA-256-Hash) gemäß EU-Verordnung 910/2014 (eIDAS) aufgezeichnet.",
    "kyc.sign_modal_checkbox": "Ich erkläre, den Inhalt des Dokuments gelesen, verstanden und vollständig akzeptiert zu haben.",
    "kyc.sign_modal_confirm": "Jetzt signieren",
    "kyc.sign_modal_cancel": "Abbrechen",
    "kyc.sign_modal_disclaimer": "Die einfache elektronische Signatur erzeugt zwischen den Parteien die Rechtswirkungen einer schriftlichen Unterschrift.",

    "calc.overline": "Renditesimulator",
    "calc.title": "Schätzen Sie Ihren Horizont.",
    "calc.intro": "Bewegen Sie den Regler, um die erwartete Rendite über die ersten 12 Monate Kapitalbindung zu simulieren. Die Werte spiegeln die aktuellen vom Borealis-Reserve-Team veröffentlichten Pakete wider.",
    "calc.amount_label": "Einzubringendes Kapital",
    "calc.duration_label": "Laufzeit",
    "calc.duration_12": "12 Monate",
    "calc.duration_24": "24 Monate",
    "calc.duration_36": "36 Monate",
    "calc.duration_open": "Open-Ended",
    "calc.tier_label": "Anwendbares Paket",
    "calc.rate_gross": "Erwartete Bruttorendite",
    "calc.rate_net": "Geschätzte jährliche Nettorendite",
    "calc.mngmt_fee": "Verwaltungsgebühr",
    "calc.perf_fee": "Performance-Gebühr",
    "calc.lock_period": "Bindungsfrist",
    "calc.gross_profit": "Bruttogewinn ({months}M)",
    "calc.net_profit": "Nettogewinn ({months}M)",
    "calc.maturity_value": "Wert bei Fälligkeit",
    "calc.chart_title": "Kapitalentwicklung",
    "calc.chart_legend": "Projektion über {months} Monate",
    "calc.chart_open_note": "Indikative 12-Monats-Projektion",
    "calc.no_plan": "Für dieses Kapital ist derzeit kein Paket aktiv. Wenden Sie sich an Ihren Relationship Manager.",
    "calc.disclaimer": "Unverbindliche Simulation zu Illustrationszwecken. Es gelten die Bedingungen des zum Zeitpunkt der Einzahlung gezeichneten Pakets.",
    "disc.title": "Regulatorische Informationen",
    "disc.past": "Die frühere Wertentwicklung ist kein verlässlicher Indikator für künftige Ergebnisse.",
    "disc.risk": "Das investierte Kapital ist einem Risiko ausgesetzt. Der Wert von Anlagen kann sowohl fallen als auch steigen.",
    "disc.mkt": "Marketing-Mitteilung. Dies stellt keine Finanzberatung oder ein öffentliches Angebot dar.",
    "nav.plans": "Pläne",
    "plans.overline": "Zeichnung",
    "plans.title": "Investitionspläne",
    "plans.intro": "Entdecken Sie die Mitgliedern vorbehaltenen Pakete mit fester Rendite. Wählen Sie einen Plan, um eine Zeichnungsanfrage an das Finance & Compliance Team zu senden.",
    "plans.loading": "Pläne werden geladen…",
    "plans.kyc_title": "Frei erkunden",
    "plans.kyc_body": "Durchstöbern Sie die Pakete, laden Sie die KIDs herunter und simulieren Sie Renditen. Sie können Ihre Zeichnungsanfrage sofort senden: Zur Aktivierung bitten wir Sie, die KYC-Verifizierung abzuschließen.",
    "plans.kyc_cta": "Zum KYC",
    "plans.partner_title": "Partnermodus",
    "plans.partner_body": "Präsentationsvitrine: Nutzen Sie Produktblätter, Renditerechner und KIDs, um Borealis Reserve Ihren Kontakten vorzustellen. Der Beitritt zu den Plänen ist ausschließlich zeichnenden Kunden vorbehalten.",
    "plans.partner_showcase_badge": "Nur Präsentation",
    "plans.partner_your_code": "Ihr Code",
    "plans.partner_copy_link": "Ihren Link kopieren →",
    "plans.range": "Einzahlungsspanne",
    "plans.checkbox": "Ich habe die Informationen gelesen und möchte diesen Plan anfragen",
    "plans.request_cta": "Zeichnung anfragen",
    "plans.pending_badge": "Anfrage ausstehend",
    "plans.amount_label": "Richtbetrag (optional)",
    "plans.amount_help": "Innerhalb der Planspanne. Der endgültige Betrag wird mit dem Team vereinbart.",
    "plans.note_label": "Anmerkung für das Team (optional)",
    "plans.modal_title": "Zeichnungsanfrage bestätigen",
    "plans.modal_body": "Mit Klick auf „Bestätigen und senden“ wird Ihre Anfrage für den Plan „{plan}“ an das Finance & Compliance Team gesendet. Sie erhalten weitere Details per E-Mail und Telefon, einschließlich — im Falle einer Annahme — der Überweisungsdaten. In dieser Phase ist keine Zahlung fällig.",
    "plans.modal_confirm": "Bestätigen und senden",
    "plans.modal_cancel": "Abbrechen",
    "plans.toast_ok": "Anfrage gesendet. Das Finance & Compliance Team wird Sie in Kürze kontaktieren.",
    "plans.toast_err": "Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.",
    "plans.my_requests": "Meine Anfragen",
    "plans.status_pending": "Ausstehend",
    "plans.status_awaiting_kyc": "Wartet auf KYC",
    "plans.toast_ok_kyc": "Anfrage gesendet. Schließen Sie jetzt die KYC-Verifizierung ab: Sobald sie genehmigt ist, geht Ihre Anfrage an das Investment-Team.",
    "plans.status_approved": "Genehmigt",
    "plans.status_rejected": "Nicht angenommen",
    "plans.no_requests": "Sie haben noch keine Anfragen gesendet.",
    "plans.kid_download": "KID herunterladen (PDF)",
    "plans.kid_ack_label": "Ich habe das Basisinformationsblatt (KID) dieses Produkts gelesen und verstanden",
    "plans.kid_modal_hint": "Bitte lesen Sie vor dem Absenden der Anfrage das Basisinformationsblatt (KID) des Produkts.",
    "plans.allocation_title": "Vermögensaufteilung",
    "plans.volatility_target": "Volatilitätsziel",
    "plans.allocation_show": "Vermögensaufteilung anzeigen",
    "plans.allocation_hide": "Vermögensaufteilung ausblenden",
    "plans.allocation_note": "Indikative Zielallokation zu Illustrationszwecken, regelmäßig durch das Borealis-Anlagekomitee neu gewichtet. Die tatsächliche Gewichtung kann je nach Marktlage variieren.",
    "alloc.global_equity_etf": "Globale Aktien-ETFs",
    "alloc.global_equity": "Globale Aktien",
    "alloc.investment_grade_bonds": "Investment-Grade-Anleihen",
    "alloc.money_market_short": "Geldmarkt & kurze Duration",
    "alloc.money_market": "Geldmarkt",
    "alloc.alternative_ucits_funds": "Alternative UCITS-Fonds",
    "alloc.alternative_ucits_strategies": "Alternative UCITS-Strategien",
    "alloc.alternative_strategies": "Alternative Strategien",
    "alloc.gold_commodities": "Gold & Rohstoffe",
    "alloc.commodities": "Rohstoffe",
    "alloc.private_markets": "Private Markets",
    "alloc.private_equity": "Private Equity",
    "alloc.private_credit": "Private Credit",
    "alloc.real_assets": "Sachwerte",
    "alloc.cash": "Barmittel",
    "alloc.tactical_cash": "Taktische Barmittel",
    "calc.months": "Monate",
    "calc.lock_open": "Open-Ended",

    "team.overline": "Verwaltungsteam",
    "team.title": "Die Menschen hinter Ihrem Kapital.",
    "team.intro": "Die Profis, die Ihre Strategie strukturieren. Regulierte Profile, überprüfbar bei FINRA.",
    "team.crd": "CRD#",
    "team.years_experience": "Jahre Erfahrung",
    "team.finra_link": "FINRA-Profil →",
    "team.locked_title": "Reservierter Bereich",
    "team.locked_body": "Das Verwaltungsteam wird sichtbar, nachdem der KYC-Prozess abgeschlossen ist. Gehen Sie zum KYC-Bereich, um Ihre Dokumente zu vervollständigen.",

    "nav.events": "Veranstaltungen",
    "events.overline": "Reservierter Kalender",
    "events.title": "Die Termine des Clubs.",
    "events.intro": "Private Dinners, Makro-Briefings, Round-Tables. Alle Treffen erfordern eine Teilnahmeanfrage, die vom Compliance-Team genehmigt wird.",
    "events.locked_title": "Reservierter Bereich",
    "events.locked_body": "Der Veranstaltungskalender wird sichtbar, nachdem der KYC-Prozess abgeschlossen ist. Gehen Sie zum KYC-Bereich, um ihn freizuschalten.",
    "events.section_upcoming": "Kommende Veranstaltungen",
    "events.section_past": "Vergangene Veranstaltungen",
    "events.empty_upcoming": "Derzeit keine Veranstaltungen im Kalender. Wir senden Ihnen eine Einladung, sobald eine veröffentlicht wird.",
    "events.empty_past": "Noch keine vergangenen Veranstaltungen zum Anzeigen.",
    "events.starts_at": "Wann",
    "events.venue": "Wo",
    "events.dress_code": "Dresscode",
    "events.capacity": "Plätze",
    "events.rsvp_request": "Teilnahme anfragen",
    "events.rsvp_cancel": "Anfrage zurückziehen",
    "events.rsvp_status_pending": "Anfrage gesendet",
    "events.rsvp_status_accepted": "Teilnahme bestätigt",
    "events.rsvp_status_declined": "Anfrage nicht angenommen",
    "events.rsvp_admin_comment": "Hinweis Compliance-Team",
    "events.rsvp_modal_title": "Teilnahmeanfrage",
    "events.rsvp_modal_intro": "Die Anfrage wird vom Compliance-Team auf Basis Ihres Profils und Ihrer Mitgliedschaftsdauer geprüft. Sie erhalten innerhalb von 2 Werktagen eine Bestätigung per E-Mail.",
    "events.rsvp_modal_plusone": "Ich erscheine mit Begleitung",
    "events.rsvp_modal_notes": "Hinweise (Ernährungsvorgaben, besondere Bedürfnisse)…",
    "events.rsvp_modal_submit": "Anfrage senden",
    "events.rsvp_modal_cancel": "Abbrechen",

    "events.criteria_overline": "Teilnahmekriterien",
    "events.criteria_title": "Clubzugehörigkeit und Veranstaltungszugang.",
    "events.criteria_intro": "Teilnahmeanfragen werden vom Compliance-Team manuell auf Basis der Clubzugehörigkeit geprüft. Nachfolgend die Referenzkriterien.",
    "events.criteria_member": "Mitglied · Zugang zu Webinaren und Online-Konferenzen",
    "events.criteria_senior": "Senior Member (3 Jahre) · zusätzlich Zugang zu Round-Tables",
    "events.criteria_legacy": "Legacy Member (5 Jahre) · zusätzlich Zugang zu privaten Dinners",
    "events.criteria_patron": "Patron Member (7 Jahre) · zusätzlich Zugang zu exklusiven Erlebnissen",
    "events.criteria_founders": "Founders Circle (10 Jahre) · absolute Priorität und Einladungen zu platzbeschränkten Events",
    "events.criteria_your_tenure": "Ihre Clubzugehörigkeit beträgt {years} Jahre · aktuelle Stufe {tier}.",

    "membership.tier.member": "Mitglied",
    "membership.tier.senior_member": "Senior Member",
    "membership.tier.legacy_member": "Legacy Member",
    "membership.tier.patron_member": "Patron Member",
    "membership.tier.founders_circle": "Founders Circle",

    "calc.tier_foundation": "Borealis Foundation",
    "calc.tier_prime": "Borealis Prime",
    "calc.tier_signature": "Borealis Signature",
    "calc.tier_sovereign": "Borealis Sovereign",
    "calc.tier_legacy": "Borealis Legacy",

    // ---------- Introducer portal ----------
    "intro.nav_dashboard": "Dashboard",
    "intro.nav_referrals": "Empfehlungen",
    "intro.nav_contract": "Vertrag",
    "intro.nav_guide": "Handbuch",
    "intro.contract_overline": "Vermittlungsvertrag",
    "intro.contract_heading_a": "Ihr Vertrag",
    "intro.contract_heading_em": "mit Borealis Reserve.",
    "intro.contract_intro": "Empfehlungs- und Vermittlungsvertrag. Unterzeichnen Sie digital, um als autorisierter Borealis-Reserve-Introducer tätig zu werden.",
    "intro.contract_status_signed": "Unterzeichnet",
    "intro.contract_status_pending": "Zu unterzeichnen",
    "intro.contract_signed_on": "Unterzeichnet am",
    "intro.contract_download": "PDF herunterladen",
    "intro.contract_sign_overline": "Digitale Signatur",
    "intro.contract_sign_heading": "Vertrag unterzeichnen",
    "intro.contract_sign_body": "Bitte lesen Sie den Vertrag oben. Durch Klicken auf \"Digital unterzeichnen\" bestätigen Sie, alle Klauseln gelesen und verstanden zu haben. Die Unterschrift ist gemäß Art. 20 CAD (ital. GvD 82/2005) rechtsverbindlich und entspricht einer schriftlichen Unterschrift (Art. 2702 ital. ZGB).",
    "intro.contract_ack_label": "Ich habe alle Vertragsartikel gelesen, verstanden und akzeptiere sie vollständig. Ich bin der Kontoinhaber und führe die digitale Unterzeichnung durch.",
    "intro.contract_sign_cta": "Digital unterzeichnen",
    "intro.contract_signing": "Signieren…",
    "intro.contract_legal_note": "IP, Zeitstempel und Hash protokolliert",
    "intro.contract_signed_overline": "Unterzeichnet",
    "intro.contract_signed_heading": "Vertrag aktiv.",
    "intro.contract_signed_body": "Eine unterzeichnete Kopie mit Zeitstempel, Quell-IP und SHA-256-Integritäts-Hash wurde Ihnen per E-Mail zugesandt. Sie können sie jederzeit hier einsehen und herunterladen.",
    "intro.contract_back_dashboard": "Zurück zum Dashboard",
    "intro.contract_back": "Introducer-Dashboard",
    "intro.contract_banner_overline_pending": "Aktion erforderlich",
    "intro.contract_banner_title_pending": "Empfehlungsvertrag unterzeichnen",
    "intro.contract_banner_body_pending": "Bevor Sie mit der Empfehlungstätigkeit beginnen, müssen Sie den Vertrag digital unterzeichnen. Dauert 60 Sekunden.",
    "intro.contract_banner_cta_pending": "Zum Vertrag",
    "intro.contract_banner_overline_signed": "Vertrag aktiv",
    "intro.contract_banner_body_signed": "Unterzeichnet am {date}. Unterzeichnete PDF per E-Mail versendet und stets im Vertragsbereich verfügbar.",
    "intro.contract_banner_view_signed": "Vertrag prüfen",
    "intro.dashboard_overline": "Partnerportal",
    "intro.dashboard_heading_a": "Willkommen,",
    "intro.dashboard_intro": "Ihr privater Bereich — sehen Sie, wie viele Kontakte Sie zu Borealis Reserve gebracht haben, das aggregierte AUM und Ihre aufgelaufenen Provisionen. Die Privatsphäre der empfohlenen Kunden ist geschützt: Namen und Details werden nie offengelegt.",
    "intro.kpi_referrals_total": "Empfehlungen gesamt",
    "intro.kpi_referrals_active_sub": "{count} aktiv",
    "intro.kpi_aum": "Eingebrachtes AUM",
    "intro.kpi_deposits_count": "{count} aktive Einlagen",
    "intro.kpi_accrued": "Aufgelaufen (netto)",
    "intro.kpi_accrued_sub": "Brutto: {gross}",
    "intro.kpi_yearly_estimate": "Jahresschätzung (netto)",
    "intro.kpi_commission_rate": "{pct}% p.a. auf AUM",
    "intro.share_overline": "Ihr Link",
    "intro.share_heading": "Teilen Sie Borealis Reserve.",
    "intro.share_body": "Jeder Kontakt, der über den untenstehenden Link kommt, wird automatisch Ihrem Promocode zugeordnet.",
    "intro.copy_link": "Link kopieren",
    "intro.copied": "Kopiert",
    "intro.code_label": "Code",
    "intro.privacy_overline": "Datenschutz Empfehlungen",
    "intro.privacy_body": "Zum Schutz der Vertraulichkeit der von Ihnen eingeführten Kunden sehen Sie nie Namen, E-Mails, Telefonnummern oder KYC-Dokumente. Sie sehen nur anonymisierte Aggregate (Anzahl, AUM, Ihre aufgelaufene Provision). Borealis verwaltet jede Geschäftsbeziehung direkt.",
    "intro.dashboard_view_statements": "Monatliche Abrechnungen",
    "intro.dashboard_view_statements_sub": "Laden Sie Ihre Provisionsabrechnungen herunter",
    "intro.stmt_overline": "Provisionen",
    "intro.stmt_heading_a": "Monatliche",
    "intro.stmt_heading_em": "Abrechnungen.",
    "intro.stmt_intro": "Am Monatsende veröffentlicht das Borealis-Team hier Ihre Provisionsabrechnung: Bruttobetrag, ggf. Quellensteuer und den ausgezahlten Nettobetrag. Jedes PDF ist gegengezeichnet und wird zu Steuerzwecken archiviert.",
    "intro.stmt_col_period": "Zeitraum",
    "intro.stmt_col_ref": "Abrechnung Nr.",
    "intro.stmt_col_gross": "Brutto",
    "intro.stmt_col_withholding": "Quellensteuer",
    "intro.stmt_col_net": "Netto",
    "intro.stmt_col_download": "Herunterladen",
    "intro.stmt_loading": "Wird geladen…",
    "intro.stmt_empty": "Noch keine Abrechnung veröffentlicht. Die erste ist ab dem Monat nach Vertragsunterzeichnung verfügbar.",
    "intro.stmt_footnote": "Steuerdaten werden gemäß den geltenden Vorschriften verarbeitet. Bei Fragen schreiben Sie an finance@borealisreserve.com.",
    "intro.dashboard_playbook": "Partner-Handbuch",
    "intro.dashboard_playbook_sub": "Schritt-für-Schritt-Anleitung zu Ihrem Partnerportal",
    "intro.dashboard_playbook_cta": "Handbuch öffnen",
    "intro.referrals_overline": "Ihre Empfehlungen",
    "intro.referrals_heading_a": "Kontakte",
    "intro.referrals_heading_em": "anonymisiert.",
    "intro.referrals_intro": "Die von Ihnen eingeführten Kunden — nur aggregierte Indikatoren. Es werden nie persönliche Daten preisgegeben.",
    "intro.referrals_loading": "Lädt…",
    "intro.referrals_empty": "Noch keine Empfehlungen — teilen Sie Ihren Link oder Promocode.",
    "intro.col_ref": "Empfehlung",
    "intro.col_activated": "Aktiviert",
    "intro.col_status": "Status",
    "intro.col_principal": "AUM-Beitrag",
    "intro.col_deposits": "Einlagen",
    "intro.status_active": "Aktiv",
    "intro.status_pending": "Ausstehend",
    "intro.status_suspended": "Gesperrt",
    "intro.referrals_privacy_footnote": "* Die Spalten sind auf den Aktivierungsmonat gerundet, um die Identität des Kunden zu schützen. Borealis kann auf schriftliche Anfrage zusätzliche zertifizierte Berichte bereitstellen.",

    // ---------- Public referral landing ----------
    "referral.brand_tagline": "Private-Client-Portal",
    "referral.checking": "Code wird überprüft…",
    "referral.invalid_overline": "Ungültiger Link",
    "referral.invalid_title": "Code nicht erkannt",
    "referral.invalid_body": "Der Promocode ist nicht mehr gültig oder wurde deaktiviert. Bitte kontaktieren Sie die Person, die Ihnen den Link geteilt hat, um einen aktualisierten zu erhalten.",
    "referral.back_to_login": "Zum Login",
    "referral.overline": "Sie wurden empfohlen",
    "referral.heading_a": "Empfohlen von",
    "referral.heading_b": ", sind Sie einen Schritt von Borealis Reserve entfernt.",
    "referral.intro": "Hinterlassen Sie uns Ihre Kontaktdaten: Ein Borealis-Berater meldet sich für ein diskretes erstes Gespräch. Ohne Verpflichtung.",
    "referral.field_name": "Vollständiger Name",
    "referral.field_email": "E-Mail",
    "referral.field_phone": "Telefon",
    "referral.field_message": "Nachricht (optional)",
    "referral.submitting": "Senden…",
    "referral.cta": "Anfrage senden",
    "referral.privacy_note": "Ihre Daten werden ausschließlich zur Bewertung Ihres Interesses an Borealis Reserve verarbeitet, gemäß EU-Verordnung 2016/679 (DSGVO).",
    "referral.thanks_overline": "Danke",
    "referral.thanks_title": "Anfrage erhalten.",
    "referral.thanks_body": "Ein Borealis-Berater wird sich in Kürze bei Ihnen melden. Für dringende Angelegenheiten schreiben Sie an info@borealisreserve.com.",
    "referral.err_required": "Name und E-Mail sind erforderlich.",
    "referral.err_generic": "Etwas ist schiefgegangen. Bitte versuchen Sie es erneut.",

  },
};

export function getString(locale, key, vars = {}) {
  const loc = normalizeLocale(locale);
  let value = STRINGS[loc]?.[key];
  if (value == null) value = STRINGS[DEFAULT_LOCALE][key] ?? key;
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)),
    value
  );
}
